import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getStoredToken } from '../services/authSession';
import {
  ArrowLeft, ArrowRight, Upload, CheckCircle, Clock, AlertCircle,
  FileText, ChevronRight, Eye, Save, Zap, Loader2, X, RefreshCw, Copy, Download
} from 'lucide-react';
import API from '../services/apiBase';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const getToken = () => getStoredToken();
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const api = async (path, opts = {}) => {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { ...authHeaders(), ...opts.headers },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback = `HTTP ${res.status}`;
    throw new Error(_formatApiDetail(err.detail, fallback) || err.message || fallback);
  }
  return res.json();
};

const _formatApiDetail = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  // FastAPI structured error: { error, hints, stage, errors }
  if (typeof detail === 'object') {
    const parts = [];
    if (detail.error) parts.push(detail.error);
    if (Array.isArray(detail.errors) && detail.errors.length) parts.push(detail.errors.join('; '));
    if (Array.isArray(detail.hints) && detail.hints.length) parts.push(`hint: ${detail.hints.join('; ')}`);
    if (parts.length) return parts.join(' — ');
    // FastAPI validation: [{loc, msg, type}, ...]
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => d?.msg || d?.message).filter(Boolean);
      if (msgs.length) return msgs.join('; ');
    }
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail) || fallback;
};

const apiUpload = async (path, file, extraFields = {}) => {
  const fd = new FormData();
  fd.append('file', file);
  Object.entries(extraFields).forEach(([k, v]) => fd.append(k, v));
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: fd,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const fallback = `HTTP ${res.status}`;
    throw new Error(_formatApiDetail(err.detail, fallback) || err.message || fallback);
  }
  return res.json();
};

// Fetch a backend-generated .xlsx template and trigger a browser download.
const downloadTemplate = async (path, filename) => {
  const res = await fetch(`${API}${path}`, { method: 'GET', headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to download template (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// ─── Phase / Step definitions ──────────────────────────────────────────────────
// Section Configuration is generated dynamically per-client based on the counts
// the user sets in Client Setup (e.g. 2 fringes, 10 allocations). Each generated
// step carries sectionType + sectionId so the shared renderer can wire it.

const SECTION_TYPE_LABELS = { ga: 'G&A', material_handling: 'M&H' };

const buildPhases = ({ fringeCount, allocationCount, overheadCount, hasGA, includeMH, sectionNames }) => {
  const sectionStep = (type, index) => {
    const id = `${type}_${index}`;
    const defaultLabel = `${SECTION_TYPE_LABELS[type] || (type[0].toUpperCase() + type.slice(1))} ${index}`;
    const userLabel = sectionNames?.[id];
    return {
      id,
      label: userLabel || defaultLabel,
      defaultLabel,
      sectionType: type,
      sectionIndex: index,
    };
  };
  const sectionSteps = [];
  for (let i = 1; i <= fringeCount; i++) sectionSteps.push(sectionStep('fringe', i));
  for (let i = 1; i <= allocationCount; i++) sectionSteps.push(sectionStep('allocation', i));
  for (let i = 1; i <= overheadCount; i++) sectionSteps.push(sectionStep('overhead', i));
  if (hasGA) sectionSteps.push(sectionStep('ga', 1));
  if (includeMH) sectionSteps.push(sectionStep('material_handling', 1));

  return [
    { id: 'foundation', label: 'Client Foundation', steps: [
      { id: 'client_setup', label: 'Client Setup' },
      { id: 'gl_upload', label: 'GL Upload' },
      { id: 'gl_mapping', label: 'GL Mapping' },
    ]},
    { id: 'build_plan', label: 'Build Plan', steps: [
      { id: 'section_plan', label: 'Section Plan' },
    ]},
    { id: 'sections', label: 'Section Configuration', steps: sectionSteps },
    { id: 'schedules', label: 'Schedule Configuration', steps: [
      { id: 'schedule_h_template', label: 'Schedule H Template' },
      { id: 'schedule_h_rules', label: 'Schedule H Rules' },
      { id: 'schedule_i_template', label: 'Schedule I Template' },
      { id: 'schedule_i_rules', label: 'Schedule I Rules' },
      { id: 'schedule_k_template', label: 'Schedule K Template' },
      { id: 'schedule_k_rules', label: 'Schedule K Rules' },
      { id: 'schedule_g_listing', label: 'Schedule G Listing' },
      { id: 'schedule_l_listing', label: 'Schedule L Listing' },
    ]},
    { id: 'additional', label: 'Additional Logic', steps: [
      // Adjustments step removed 2026-07-09: pool/base adjustments are
      // handled in production after activation, not during onboarding.
      { id: 'adequacy_checks', label: 'Adequacy Checks' },
    ]},
    { id: 'finalization', label: 'Finalization', steps: [
      { id: 'review', label: 'Review Config' },
      { id: 'activate', label: 'Activate' },
    ]},
  ];
};

// ─── StatusBadge ───────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const cfg = {
    VALID: { bg: 'bg-green-100', text: 'text-green-800', label: 'Valid' },
    STALE: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Stale' },
    ERROR: { bg: 'bg-red-100', text: 'text-red-800', label: 'Error' },
  }[status] || { bg: 'bg-gray-100', text: 'text-gray-600', label: status };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

// A section's pool/base can be built either from a flat account list
// (accounts non-empty) or entirely from poolModifiers targeting that side
// (scoped_sum/apply_rate/include_overhead/include_adjustment dependencies,
// each self-contained — no upstream section needed). An empty accounts list
// with no modifiers-fallback renders as "[]", which reads as "the base is
// empty" even when it's fully and correctly built via dependencies —
// this describes what actually populates that side either way.
const describeAccountsOrModifiers = (accounts, poolModifiers, side) => {
  if (accounts?.length) return `[${accounts.join(', ')}]`;
  const deps = (poolModifiers || []).filter((m) => (m.target || 'pool') === side);
  if (deps.length) {
    const refIds = deps.map((d) => d.dependency?.refId).filter(Boolean);
    return `(built from ${deps.length} dependenc${deps.length === 1 ? 'y' : 'ies'}: ${refIds.join(', ')})`;
  }
  return '[]';
};

// ─── DependencyPanel ──────────────────────────────────────────────────────────

const DependencyPanel = ({ preview }) => {
  if (!preview?.dependencies?.length) return null;
  const statusIcon = { RESOLVED: '✓', STALE: '⚠', MISSING: '✕' };
  const statusColor = { RESOLVED: 'text-green-600', STALE: 'text-amber-600', MISSING: 'text-red-600' };
  return (
    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Dependencies Detected</p>
      <div className="space-y-2">
        {preview.dependencies.map((dep, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <div>
              <span className="font-mono text-xs text-gray-500">{dep.refId}</span>
              <span className="ml-2 font-medium">{dep.name}</span>
              <span className="ml-2 text-xs text-gray-500">— {dep.usage}</span>
            </div>
            <span className={`font-medium text-xs ${statusColor[dep.status] || 'text-gray-500'}`}>
              {statusIcon[dep.status]} {dep.status}
            </span>
          </div>
        ))}
      </div>
      <div className={`mt-3 pt-3 border-t text-xs font-medium ${preview.allDependenciesResolved ? 'text-green-600' : 'text-amber-600'}`}>
        All dependencies resolved: {preview.allDependenciesResolved ? 'YES' : 'NO'}
      </div>
    </div>
  );
};

// ─── PoolBreakdownPanel ────────────────────────────────────────────────────────
// Shows how calcResult.poolSum was built: the raw GL query, then each
// dependency's contribution (fringe/overhead/allocation burdens), so a pool
// total that looks too high can be traced to a specific line item — e.g. an
// include_allocation dependency that fell back to a full upstream pool
// instead of a specific receiver's share.

const PoolBreakdownPanel = ({ calcResult }) => {
  const deps = calcResult?.dependencyBreakdown || [];
  if (!deps.length) return null;
  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="bg-white border rounded p-2 mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-2">Pool breakdown</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-gray-600">
            <th className="text-left py-1">Source</th>
            <th className="text-left py-1">Action</th>
            <th className="text-right py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="py-1 text-gray-600">Raw GL pool query</td>
            <td className="py-1 text-gray-500">{calcResult.poolRowCount} rows</td>
            <td className="py-1 text-right font-mono">{fmt(calcResult.poolSumRaw)}</td>
          </tr>
          {deps.filter((d) => (d.target || 'pool') === 'pool').map((d, i) => {
            const isFullPoolFallback = d.action === 'include_allocation'
              && d.resolved?.poolSum != null && d.resolved?.receiverId == null;
            return (
              <tr key={i} className={`border-b last:border-0 ${isFullPoolFallback ? 'bg-amber-50' : ''}`}>
                <td className="py-1">
                  <code className="text-blue-700">{d.refId}</code>
                  {isFullPoolFallback && (
                    <span className="ml-1 text-amber-700" title="No receiver matched — full upstream pool included">
                      ⚠ full pool
                    </span>
                  )}
                </td>
                <td className="py-1 text-gray-600">
                  {d.action}
                  {d.resolved?.receiverId != null && (
                    <span className="ml-1 text-gray-400 font-mono">
                      [{Array.isArray(d.resolved.receiverId) ? d.resolved.receiverId.join(', ') : d.resolved.receiverId}]
                    </span>
                  )}
                </td>
                <td className="py-1 text-right font-mono">{fmt(d.amount)}</td>
              </tr>
            );
          })}
          <tr className="font-semibold">
            <td className="py-1" colSpan={2}>Total pool sum</td>
            <td className="py-1 text-right font-mono">{fmt(calcResult.poolSum)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ─── BaseBreakdownPanel ────────────────────────────────────────────────────────
// Same idea as PoolBreakdownPanel, mirrored for the Base side — a fringe/
// overhead/G&A section's base-side dependencies (e.g. "Apply each OH rate to
// Direct Labor and Direct Labor Fringe for each division") were previously
// invisible in the wizard, making a too-low Base sum impossible to diagnose.

const BaseBreakdownPanel = ({ calcResult }) => {
  const deps = (calcResult?.dependencyBreakdown || []).filter((d) => d.target === 'base');
  const hasBaseQuery = calcResult?.baseSumRaw != null;
  if (!deps.length && !hasBaseQuery) return null;
  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="bg-white border rounded p-2 mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-2">Base breakdown</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-gray-600">
            <th className="text-left py-1">Source</th>
            <th className="text-left py-1">Action</th>
            <th className="text-right py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          {hasBaseQuery && (
            <tr className="border-b">
              <td className="py-1 text-gray-600">Raw GL base query</td>
              <td className="py-1 text-gray-500">{calcResult.baseRowCount} rows</td>
              <td className="py-1 text-right font-mono">{fmt(calcResult.baseSumRaw)}</td>
            </tr>
          )}
          {deps.map((d, i) => {
            const isFullPoolFallback = d.action === 'include_allocation'
              && d.resolved?.poolSum != null && d.resolved?.receiverId == null;
            return (
              <tr key={i} className={`border-b last:border-0 ${isFullPoolFallback ? 'bg-amber-50' : ''}`}>
                <td className="py-1">
                  <code className="text-blue-700">{d.refId}</code>
                  {isFullPoolFallback && (
                    <span className="ml-1 text-amber-700" title="No receiver matched — full upstream pool included">
                      ⚠ full pool
                    </span>
                  )}
                </td>
                <td className="py-1 text-gray-600">
                  {d.action}
                  {d.resolved?.receiverId != null && (
                    <span className="ml-1 text-gray-400 font-mono">
                      [{Array.isArray(d.resolved.receiverId) ? d.resolved.receiverId.join(', ') : d.resolved.receiverId}]
                    </span>
                  )}
                </td>
                <td className="py-1 text-right font-mono">{fmt(d.amount)}</td>
              </tr>
            );
          })}
          <tr className="font-semibold">
            <td className="py-1" colSpan={2}>Total base sum</td>
            <td className="py-1 text-right font-mono">{fmt(calcResult.baseSum ?? calcResult.totalBaseSum)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// ─── FileUploadBox ─────────────────────────────────────────────────────────────

const FileUploadBox = ({ label, accept, onUpload, uploading, result, existing }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = React.useRef();

  const handleFile = (file) => {
    if (file) onUpload(file);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
          ${existing ? 'bg-green-50 border-green-300' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-blue-600">
            <Loader2 className="w-5 h-5 animate-spin" /> Uploading & extracting...
          </div>
        ) : existing ? (
          <div className="text-green-700">
            <CheckCircle className="w-8 h-8 mx-auto mb-2" />
            <p className="font-medium">{existing}</p>
            <p className="text-xs mt-1">Click or drop to replace</p>
          </div>
        ) : (
          <div className="text-gray-500">
            <Upload className="w-8 h-8 mx-auto mb-2" />
            <p>Drop file here or click to browse</p>
            <p className="text-xs mt-1">{accept || '.xlsx, .xls'}</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept || '.xlsx,.xls'}
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </div>
      {result?.error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {result.error}
        </div>
      )}
      {result?.success && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
          {result.success}
        </div>
      )}
    </div>
  );
};

// ─── ExtractionPreview ─────────────────────────────────────────────────────────

const ExtractionPreview = ({ title, data }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;

  return (
    <div className="mt-4 border border-gray-200 rounded-lg">
      <button
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="font-medium text-sm">{title}</span>
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 max-h-96 overflow-auto">
          <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ─── SectionBadge ──────────────────────────────────────────────────────────────

const SectionBadge = ({ count, label, present }) => (
  <div className={`px-3 py-2 rounded-lg text-sm ${present ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
    <span className="font-bold">{count}</span> {label}
    {present && <CheckCircle className="w-3 h-3 inline ml-1" />}
  </div>
);

// ─── AIExtractBox ──────────────────────────────────────────────────────────────
//
// Drop a real client workbook tab in here and Claude proposes a structured
// schema entity (FringeDefinition / AllocationDefinition / etc.). Read-only —
// the proposal is for human review. Wiring confirmed proposals through to the
// compile pipeline is a separate step.

const ConfidenceBadge = ({ level }) => {
  const cls = level === 'high' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : level === 'medium' ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-red-100 text-red-800 border-red-300';
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${cls}`}>
      AI confidence: {level}
    </span>
  );
};

const AccountChips = ({ accounts, label }) => {
  if (!accounts || accounts.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-1">{label} ({accounts.length})</div>
      <div className="flex flex-wrap gap-1">
        {accounts.slice(0, 20).map((a, i) => (
          <code key={i} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded border">{a}</code>
        ))}
        {accounts.length > 20 && <span className="text-xs text-gray-500">+{accounts.length - 20} more</span>}
      </div>
    </div>
  );
};

const FilterList = ({ filters, label = 'Filters' }) => {
  if (!filters || filters.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-1">{label} ({filters.length})</div>
      <div className="space-y-1">
        {filters.map((f, i) => (
          <div key={i} className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border">
            <span className="text-blue-700">{f.field}</span>{' '}
            <span className="text-gray-500">{f.operator}</span>{' '}
            <span className="text-emerald-700">
              {Array.isArray(f.value) ? `[${f.value.join(', ')}]` : String(f.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DependencyList = ({ deps }) => {
  if (!deps || deps.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-1">Dependencies ({deps.length})</div>
      <div className="space-y-1">
        {deps.map((d, i) => (
          <div key={i} className="text-xs bg-gray-50 px-2 py-1 rounded border">
            <span className="font-mono text-gray-600">{d.type}:</span>{' '}
            <code className="text-blue-700">{d.refId}</code>
            {d.action && <span className="ml-2 text-gray-500">→ {d.action}</span>}
            {d.filter && (
              <span className="ml-2 text-xs">
                where <span className="text-blue-700">{d.filter.field}</span>{' '}
                {d.filter.operator}{' '}
                <span className="text-emerald-700">{String(d.filter.value)}</span>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ReceiverList = ({ receivers }) => {
  if (!receivers || receivers.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs font-semibold text-gray-700 mb-1">Receivers ({receivers.length})</div>
      <div className="space-y-1">
        {receivers.map((r, i) => (
          <div key={i} className="text-xs bg-gray-50 px-2 py-1 rounded border">
            <code className="text-blue-700">{r.receiverId}</code>{' '}
            <span className="text-gray-700">{r.receiverLabel}</span>
            {typeof r.manualShare === 'number' && (
              <span className="ml-2 text-gray-500">{(r.manualShare * 100).toFixed(1)}%</span>
            )}
            {r.filters?.length > 0 && (
              <div className="ml-3 mt-1">
                {r.filters.map((f, j) => (
                  <div key={j} className="text-xs font-mono text-gray-600">
                    where {f.field} {f.operator} {Array.isArray(f.value) ? `[${f.value.join(',')}]` : f.value}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const DiagnosticPanel = ({ diag }) => {
  if (!diag) return null;
  return (
    <div className="mt-2 text-xs bg-red-50 border border-red-300 rounded p-3">
      <div className="font-semibold text-red-900 mb-2 flex items-center gap-1">
        <AlertCircle className="w-4 h-4" />
        Why no GL rows matched — diagnostic
      </div>
      <div className="space-y-2 text-red-900">
        <div>
          <strong>Querying client_id:</strong> <code className="bg-white px-1 rounded">{diag.clientIdQueried}</code>
          <span className="ml-2">— total GL rows for this client: <strong>{
            typeof diag.totalRowsForClient === 'number'
              ? diag.totalRowsForClient.toLocaleString()
              : (diag.totalRowsForClient ?? 'unknown')
          }</strong></span>
        </div>
        {diag.hint && (
          <div className="bg-white border border-red-300 rounded p-2">
            <div className="font-semibold mb-1">💡 Hint:</div>
            <div>{diag.hint}</div>
          </div>
        )}
        {diag.proposedSample?.length > 0 && (
          <div>
            <div className="font-semibold mb-1">AI proposed (sample of 5):</div>
            <div className="flex flex-wrap gap-1">
              {diag.proposedSample.map((a, i) => (
                <code key={i} className="bg-yellow-100 px-1.5 py-0.5 rounded border border-yellow-300">{a}</code>
              ))}
            </div>
          </div>
        )}
        {diag.sampleAccountIds?.length > 0 && (
          <div>
            <div className="font-semibold mb-1">Actual GL accounts (sample of {diag.sampleAccountIds.length}):</div>
            <div className="flex flex-wrap gap-1">
              {diag.sampleAccountIds.map((a, i) => (
                <code key={i} className="bg-green-100 px-1.5 py-0.5 rounded border border-green-300">{a}</code>
              ))}
            </div>
          </div>
        )}
        {diag.sampleAccountIdsByPrefix && Object.keys(diag.sampleAccountIdsByPrefix).length > 0 && (
          <div>
            <div className="font-semibold mb-1">Actual GL account prefixes (top 10):</div>
            <div className="flex flex-wrap gap-1">
              {Object.entries(diag.sampleAccountIdsByPrefix).map(([p, c], i) => (
                <code key={i} className="bg-blue-100 px-1.5 py-0.5 rounded border border-blue-300">
                  {p}* ({c})
                </code>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AIExtractBox = ({ clientId, sectionType, sectionId, sectionName, state, onChange, glImporting }) => {
  // sectionId is the persistence key (e.g. 'fringe_1'); sectionType is just
  // the URL prefix for routing to the right AI extractor. They're the same
  // for legacy single-section mode (sectionId defaults to sectionType).
  const persistKey = sectionId || sectionType;
  const inputRef = useRef();
  // Local-only state: file input + in-flight loading flags + UI toggle. These
  // SHOULD reset when the user navigates between sections — handled by the
  // key={sectionType} prop on the parent that forces a fresh mount.
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  // Persisting state lives in the wizard, keyed by sectionType, so the sidebar
  // can read it and so each section keeps its own copy across navigation.
  const proposal = state?.proposal || null;
  const compiledRule = state?.compiledRule || null;
  const error = state?.error || null;
  const calcResult = state?.calcResult || null;
  const calcError = state?.calcError || null;

  const reset = () => {
    setFile(null);
    onChange({ proposal: null, compiledRule: null, error: null, calcResult: null, calcError: null });
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleCalculate = async () => {
    if (!proposal) return;
    setCalcLoading(true);
    onChange({ calcError: null, calcResult: null });
    try {
      const data = await api(`/compiled-onboarding/${clientId}/${sectionType}/calculate-from-proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposal, section_id: persistKey }),
      });
      if (data?.status === 'accepted' && data?.job_id) {
        // Calculation runs as a background job (a complex base/pool scan
        // can push the GL query past Azure's ~230s edge timeout). Poll
        // section-calculate-status until it lands.
        const TERMINAL = new Set(['completed', 'error', 'stale']);
        const MAX_POLLS = 300; // ~10 min at 2s
        let done = false;
        for (let i = 0; i < MAX_POLLS && !done; i++) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 2000));
          let res;
          try {
            // eslint-disable-next-line no-await-in-loop
            res = await api(`/compiled-onboarding/${clientId}/section-calculate-status/${data.job_id}`);
          } catch (pollErr) {
            continue; // transient — keep polling
          }
          if (TERMINAL.has(res.status)) {
            done = true;
            if (res.status === 'completed') {
              onChange({ calcResult: res.response });
            } else {
              onChange({ calcError: res.error || 'Calculation did not complete. Try again.' });
            }
          }
        }
        if (!done) {
          onChange({ calcError: 'Calculation is taking unusually long. It may still finish — try again shortly.' });
        }
      } else {
        // Backwards-compat: a synchronous response (older backend).
        onChange({ calcResult: data });
      }
    } catch (err) {
      onChange({ calcError: err.message || String(err) });
    } finally {
      setCalcLoading(false);
    }
  };

  const sectionRateLabel = sectionType === 'allocation' ? 'allocation' :
                           sectionType === 'overhead' ? 'overhead rate' :
                           sectionType === 'material_handling' ? 'M&H rate' :
                           sectionType === 'ga' ? 'G&A rate' :
                           'fringe rate';

  const handleExtract = async () => {
    if (!file) return;
    setLoading(true);
    onChange({ error: null, proposal: null, compiledRule: null, calcResult: null, calcError: null });
    try {
      const extra = { section_id: persistKey };
      if (sectionName) extra.section_name = sectionName;
      // Extraction runs as a background job (a dense section workbook can push
      // the AI call past Azure's ~230s edge timeout). Upload returns a job id;
      // poll section-extract-status until it lands.
      const data = await apiUpload(
        `/compiled-onboarding/${clientId}/${sectionType}/extract-with-ai`,
        file,
        extra,
      );
      if (data?.status === 'accepted' && data?.job_id) {
        const TERMINAL = new Set(['completed', 'error', 'stale']);
        const MAX_POLLS = 300; // ~10 min at 2s
        let done = false;
        for (let i = 0; i < MAX_POLLS && !done; i++) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 2000));
          let res;
          try {
            // eslint-disable-next-line no-await-in-loop
            res = await api(`/compiled-onboarding/${clientId}/section-extract-status/${data.job_id}`);
          } catch (pollErr) {
            continue; // transient — keep polling
          }
          if (TERMINAL.has(res.status)) {
            done = true;
            if (res.status === 'completed') {
              onChange({ proposal: res.proposal, compiledRule: res.compiledRule || null });
            } else {
              onChange({ error: res.error || 'AI extraction did not complete. Try the upload again.' });
            }
          }
        }
        if (!done) {
          onChange({ error: 'Extraction is taking unusually long. It may still finish — try again shortly.' });
        }
      } else {
        // Backwards-compat: a synchronous response (older backend).
        onChange({ proposal: data.proposal, compiledRule: data.compiledRule || null });
      }
    } catch (err) {
      onChange({ error: err?.message || String(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-2 border-blue-200 bg-blue-50/30 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-blue-600" />
        <h4 className="font-semibold text-sm">AI extract from raw workbook tab</h4>
        <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">claude-opus-4-7</span>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Drop your real client workbook tab (with prose like "where Org Level 4 is not 119"). Claude
        will propose a structured {sectionType} definition. Read-only preview — not yet wired into save.
      </p>
      <div className="flex items-center gap-2 mb-3">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            onChange({ proposal: null, compiledRule: null, error: null, calcResult: null, calcError: null });
          }}
          className="text-sm flex-1"
        />
        <button
          onClick={handleExtract}
          disabled={!file || loading}
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {loading ? 'Extracting…' : 'Extract with AI'}
        </button>
        {(file || proposal) && (
          <button onClick={reset} className="text-xs text-gray-500 hover:text-gray-800">Clear</button>
        )}
      </div>
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-2">
          {error}
        </div>
      )}
      {proposal && (
        <div className="bg-white border rounded p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-800">
              Proposed: <span className="text-blue-700">{proposal.name || '(unnamed)'}</span>
            </div>
            <ConfidenceBadge level={proposal.confidence || 'medium'} />
          </div>
          <AccountChips accounts={proposal.poolAccounts} label="Pool accounts" />
          <AccountChips accounts={proposal.baseAccounts} label="Base accounts" />
          <FilterList filters={proposal.filters} label="Filters" />
          <FilterList filters={proposal.poolFilters} label="Pool filters" />
          {proposal.basisType && (
            <div className="mb-2 text-xs">
              <span className="font-semibold text-gray-700">Basis:</span>{' '}
              <code className="bg-gray-100 px-1 rounded">{proposal.basisType}</code>
            </div>
          )}
          <ReceiverList receivers={proposal.receiverGroups} />
          <DependencyList deps={proposal.dependencies} />
          {proposal.assumptions?.length > 0 && (
            <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
              <div className="text-xs font-semibold text-amber-900 mb-1">AI assumptions made:</div>
              <ul className="text-xs list-disc ml-5 text-amber-900 space-y-0.5">
                {proposal.assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          {proposal.sourceCells?.length > 0 && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <div className="text-xs font-semibold text-blue-900 mb-1">Pulled from:</div>
              <ul className="text-xs list-disc ml-5 text-blue-900 space-y-0.5">
                {proposal.sourceCells.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">
                Validate against GL: compute the actual {sectionRateLabel}
              </span>
              <button
                onClick={handleCalculate}
                disabled={calcLoading || !proposal || glImporting}
                title={glImporting ? 'GL import is still processing — rate calculations unlock once it finishes.' : undefined}
                className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center gap-1"
              >
                {calcLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {calcLoading ? 'Calculating…' : `Calculate ${sectionRateLabel} from GL`}
              </button>
            </div>
            {glImporting && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 mb-2">
                GL import is still processing. This calculation unlocks once the GL rows finish syncing.
              </div>
            )}
            {calcError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-2">
                {calcError}
              </div>
            )}
            {calcResult && sectionType !== 'allocation' && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded p-3">
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <div>
                    <div className="text-xs uppercase text-blue-700 font-semibold mb-1">Pool sum</div>
                    <div className="text-lg font-bold font-mono">
                      ${(calcResult.poolSum || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{calcResult.poolRowCount} GL rows</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-blue-700 font-semibold mb-1">Base sum</div>
                    <div className="text-lg font-bold font-mono">
                      ${(calcResult.baseSum || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{calcResult.baseRowCount} GL rows</div>
                  </div>
                  <div className="border-l border-blue-300 pl-3">
                    <div className="text-xs uppercase text-blue-700 font-semibold mb-1">{sectionRateLabel}</div>
                    <div className="text-2xl font-bold text-indigo-700 font-mono">
                      {calcResult.ratePct || '—'}
                    </div>
                    {calcResult.rate != null && (
                      <div className="text-xs text-gray-500 mt-0.5 font-mono">
                        {calcResult.rate.toFixed(6)}
                      </div>
                    )}
                  </div>
                </div>
                <PoolBreakdownPanel calcResult={calcResult} />
                <BaseBreakdownPanel calcResult={calcResult} />
                {calcResult.filtersApplied > 0 && (
                  <div className="text-xs text-gray-600 mb-1">
                    {calcResult.filtersApplied} filter(s) applied
                  </div>
                )}
                {calcResult.filtersSkipped?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-1">
                    <strong>Skipped filters</strong> (could not translate to GL query):
                    <ul className="list-disc ml-4 mt-1">
                      {calcResult.filtersSkipped.map((s, i) => <li key={i} className="font-mono">{s}</li>)}
                    </ul>
                  </div>
                )}
                {calcResult.warnings?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <ul className="list-disc ml-4">
                      {calcResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {calcResult.diagnostic && <DiagnosticPanel diag={calcResult.diagnostic} />}
              </div>
            )}
            {calcResult && sectionType === 'allocation' && (() => {
              const baseUnit = calcResult.baseUnit || 'dollars';
              const baseSumLabel = baseUnit === 'dollars' ? 'Total base sum'
                : calcResult.basisType === 'headcount' ? 'Total headcount'
                : calcResult.basisType === 'square_footage' ? 'Total sq ft'
                : 'Workbook shares';
              const fmtBaseTotal = baseUnit === 'dollars'
                ? `$${(calcResult.totalBaseSum || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : baseUnit === 'count'
                  ? Math.round(calcResult.totalBaseSum || 0).toLocaleString()
                  : `${((calcResult.totalBaseSum || 0) * 100).toFixed(2)}%`;
              const fmtBaseRow = (val) => baseUnit === 'dollars'
                ? `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : Math.round(val).toLocaleString();
              return (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded p-3">
                <div className="grid grid-cols-2 gap-3 text-center mb-3">
                  <div>
                    <div className="text-xs uppercase text-blue-700 font-semibold mb-1">Pool sum (to allocate)</div>
                    <div className="text-lg font-bold font-mono">
                      ${(calcResult.poolSum || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {calcResult.poolRowCount} GL rows · basis: <code>{calcResult.basisType}</code>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-blue-700 font-semibold mb-1">{baseSumLabel}</div>
                    <div className="text-lg font-bold font-mono">{fmtBaseTotal}</div>
                    <div className="text-xs text-gray-500 mt-0.5">across all receivers</div>
                  </div>
                </div>
                <PoolBreakdownPanel calcResult={calcResult} />
                <BaseBreakdownPanel calcResult={calcResult} />
                <div className="bg-white border rounded p-2 mb-2">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Per-receiver allocation</div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-gray-600">
                        <th className="text-left py-1">Receiver</th>
                        <th className="text-right py-1">{baseUnit === 'dollars' ? 'Base sum' : baseSumLabel}</th>
                        <th className="text-right py-1">Share</th>
                        <th className="text-right py-1">Allocated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(calcResult.receivers || []).map((r, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1">
                            <code className="text-blue-700">{r.receiverId}</code>
                            <span className="text-gray-600 ml-1">{r.receiverLabel}</span>
                          </td>
                          <td className="py-1 text-right font-mono">
                            {r.baseSum != null ? fmtBaseRow(r.baseSum) : '—'}
                          </td>
                          <td className="py-1 text-right font-mono">
                            {r.share != null ? `${(r.share * 100).toFixed(2)}%` : '—'}
                          </td>
                          <td className="py-1 text-right font-mono font-semibold text-indigo-700">
                            {r.allocation != null ? `$${r.allocation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {calcResult.warnings?.length > 0 && (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <ul className="list-disc ml-4">
                      {calcResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                )}
                {calcResult.diagnostic && <DiagnosticPanel diag={calcResult.diagnostic} />}
              </div>
              );
            })()}
          </div>

          {compiledRule && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                className="text-xs text-blue-700 hover:text-blue-900 font-semibold"
                onClick={() => setShowRaw(!showRaw)}
              >
                {showRaw ? '▾ Hide' : '▸ Show'} generated rule (JSON) — what would be saved
              </button>
              {showRaw && (
                <div className="mt-2">
                  <div className="text-xs text-gray-600 mb-1">
                    This is the structured rule the system would persist if you approve. It has the
                    formula, IDs, source artifact metadata, and any pool modifiers / receiver groups
                    the AI extracted.
                  </div>
                  <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-auto max-h-96">
                    {JSON.stringify(compiledRule, null, 2)}
                  </pre>
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-800">
                      View raw AI proposal (pre-compile)
                    </summary>
                    <pre className="text-xs bg-gray-50 text-gray-700 p-2 rounded mt-1 overflow-auto max-h-64 border">
                      {JSON.stringify(proposal, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Wizard Component ─────────────────────────────────────────────────────

const OnboardingWizard = ({ clientId, onCancel }) => {
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [intake, setIntake] = useState(null);
  const [compiledConfig, setCompiledConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Client profile (Screen 1) — auto-saved with 500ms debounce
  const [clientName, setClientName] = useState('');
  const [fiscalYear, setFiscalYear] = useState('');
  const [notes, setNotes] = useState('');

  // GL metadata (Screen 2) — stored locally
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [sourceSystem, setSourceSystem] = useState('');
  const [versionLabel, setVersionLabel] = useState('');

  // Per-client section counts (set in Client Setup, drive dynamic step
  // generation in the wizard). Schedules / adequacy stay as toggles in
  // enabledModules — they're single-step regardless.
  const [fringeCount, setFringeCount] = useState(1);
  const [allocationCount, setAllocationCount] = useState(1);
  const [overheadCount, setOverheadCount] = useState(1);
  const [hasGA, setHasGA] = useState(true);
  // Material & Handling is optional — not every contractor has an M&H pool.
  // When checked, a dedicated M&H section step (upload + AI extract) appears,
  // and the pool compiles to a first-class material_handling section instead
  // of being faked as an extra overhead pool.
  const [includeMH, setIncludeMH] = useState(false);
  // sectionNames is a map { 'fringe_1': 'Fringe Pool 100', ... } so the
  // sidebar can show meaningful labels and the persisted intake remembers
  // names across refresh.
  const [sectionNames, setSectionNames] = useState({});

  // Single-step module toggles (schedules/adequacy).
  const [enabledModules, setEnabledModules] = useState({
    schedule_h: true, schedule_i: false,
    schedule_k: false, adequacy_check: true,
  });

  // Gap 4 — section status tracking
  const [sectionStatuses, setSectionStatuses] = useState([]);

  // Gap 5 — dependency previews
  const [dependencyPreviews, setDependencyPreviews] = useState({});

  // Gap 6 — version history
  const [versionHistory, setVersionHistory] = useState([]);
  const [cloning, setCloning] = useState(false);

  // Per-section revalidating map
  const [revalidating, setRevalidating] = useState({});

  // Upload / compile / save / activate loading
  const [uploading, setUploading] = useState({});
  const [uploadResults, setUploadResults] = useState({});
  // Async GL upload job progress (status: storing|scanning|syncing|completed|error).
  const [glJob, setGlJob] = useState(null);
  // True while a GL import is in flight — drives the hard gate on rate calcs.
  // Sourced from the persisted intake status (survives refresh) and the local
  // poll (immediate feedback within the session).
  const glImporting = intake?.gl_import_status === 'processing'
    || !!(glJob && !['completed', 'error'].includes(glJob.status));
  const [compiling, setCompiling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  // Soft parity warning from activation (2026-07-09 decision): activation
  // proceeds even when production numbers diverge from the wizard's, and
  // the mismatches are shown here for the analyst to review.
  const [parityWarning, setParityWarning] = useState(null);
  const [activationDone, setActivationDone] = useState(false);

  // AI extraction state, lifted out of AIExtractBox so it (a) survives section
  // navigation per-section without leaking across sections and (b) lets the
  // sidebar reflect "this section had a successful AI extract" without
  // requiring a Save round-trip yet.
  // Shape: { fringe: { proposal, compiledRule, error, calcResult, calcError }, allocation: {...}, ... }
  const [sectionAIState, setSectionAIState] = useState({});
  const updateSectionAI = useCallback((sectionType, updates) => {
    setSectionAIState((prev) => ({
      ...prev,
      [sectionType]: { ...(prev[sectionType] || {}), ...updates },
    }));
  }, []);

  // Per-section name updater. sectionId is the dynamic key (fringe_1 etc.);
  // names are persisted via the autosave loop above.
  const updateSectionName = useCallback((sectionId, name) => {
    setSectionNames((prev) => ({ ...prev, [sectionId]: name }));
  }, []);

  // Derive the phase tree + flattened step list from the per-client counts.
  // useMemo so step indices stay stable until counts change.
  const phases = useMemo(
    () => buildPhases({ fringeCount, allocationCount, overheadCount, hasGA, includeMH, sectionNames }),
    [fringeCount, allocationCount, overheadCount, hasGA, includeMH, sectionNames],
  );
  const ALL_STEPS = useMemo(() => phases.flatMap((p) => p.steps), [phases]);
  const currentStep = ALL_STEPS[currentStepIdx] || ALL_STEPS[0];

  // ─── Load intake on mount ──────────────────────────────────────────────────

  const loadIntake = useCallback(async () => {
    try {
      // Fetch the client record in parallel so we can show its name even
      // when the intake hasn't been saved with a client_name yet (the
      // create-client form populates clients.name; the wizard's Client
      // Setup step shouldn't re-ask for it).
      const clientRecordPromise = api(`/clients/${clientId}`).catch(() => null);

      const data = await api(`/intake/v2/list?client_id=${clientId}`);
      const intakes = data.intakes || data;
      let found = Array.isArray(intakes) ? intakes.find((i) => i.client_id === clientId) : null;

      if (!found) {
        try {
          found = await api(`/intake/v2/initiate?client_id=${clientId}`, { method: 'POST' });
        } catch (createErr) {
          console.warn('Could not auto-create intake:', createErr.message);
        }
      }

      const clientRecord = await clientRecordPromise;
      // Client record name wins as the canonical display name; intake's
      // client_name acts as a fallback for older intakes without a record.
      if (clientRecord?.name) {
        setClientName(clientRecord.name);
      }

      if (found) {
        setIntake(found);
        if (found.enabled_modules) {
          setEnabledModules((prev) => ({ ...prev, ...found.enabled_modules }));
        }
        // Per-section counts (drive dynamic step generation).
        if (typeof found.fringe_pool_count === 'number') setFringeCount(found.fringe_pool_count);
        if (typeof found.allocation_count === 'number') setAllocationCount(found.allocation_count);
        if (typeof found.oh_division_count === 'number') setOverheadCount(found.oh_division_count);
        if (typeof found.has_ga === 'boolean') setHasGA(found.has_ga);
        if (typeof found.include_material_handling === 'boolean') setIncludeMH(found.include_material_handling);
        if (found.section_names && typeof found.section_names === 'object') {
          setSectionNames(found.section_names);
        }
        // Hydrate per-section AI state from the persisted intake so a page
        // refresh restores extracted proposals + calc results without
        // requiring the user to re-run extraction.
        if (found.section_ai_proposals && typeof found.section_ai_proposals === 'object') {
          setSectionAIState(found.section_ai_proposals);
        }
        if (!clientRecord?.name && found.client_name) setClientName(found.client_name);
        if (found.fiscal_year) setFiscalYear(found.fiscal_year);
        if (found.notes) setNotes(found.notes);
        if (found.reporting_period) setReportingPeriod(found.reporting_period);
        if (found.source_system) setSourceSystem(found.source_system);
        if (found.version_label) setVersionLabel(found.version_label);
        if (found.rules_extraction?.pool_structure?.length > 0) {
          setCurrentStepIdx((prev) => Math.max(prev, 3));
        } else if (found.gl_mapping_file_id || found.mapping_file_id) {
          setCurrentStepIdx((prev) => Math.max(prev, 3));
        } else if (found.gl_file_id) {
          setCurrentStepIdx((prev) => Math.max(prev, 2));
        }
      }
    } catch (err) {
      console.warn('Could not load existing intake:', err.message);
    }
    setLoading(false);
  }, [clientId]);

  // ─── Load dependency previews ─────────────────────────────────────────────

  const loadDependencyPreviews = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await api(`/compiled-onboarding/${clientId}/dependency-preview`);
      setDependencyPreviews(result.dependencyPreviews || result || {});
    } catch (err) {
      console.warn('Could not load dependency previews:', err.message);
    }
  }, [clientId]);

  // ─── Load version history ─────────────────────────────────────────────────

  const loadVersionHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const result = await api(`/compiled-onboarding/${clientId}/history`);
      setVersionHistory(result.versions || []);
    } catch (err) {
      console.warn('Could not load version history:', err.message);
    }
  }, [clientId]);

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => { loadIntake(); }, [loadIntake]);

  useEffect(() => {
    if (currentStep.id === 'activate') loadVersionHistory();
  }, [currentStep.id, loadVersionHistory]);

  // ─── Navigation ───────────────────────────────────────────────────────────

  const goNext = () => {
    if (currentStepIdx < ALL_STEPS.length - 1) setCurrentStepIdx(currentStepIdx + 1);
  };
  const goPrev = () => {
    if (currentStepIdx > 0) setCurrentStepIdx(currentStepIdx - 1);
  };
  const goToStep = (idx) => setCurrentStepIdx(idx);

  // ─── Compile ──────────────────────────────────────────────────────────────

  const handleCompile = async () => {
    setCompiling(true);
    setError('');
    try {
      const result = await api('/compiled-onboarding/compile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, intake_id: intake?.id }),
      });
      setCompiledConfig(result);
      setSectionStatuses(result.sectionStatuses || []);
      setDependencyPreviews((prev) => ({ ...prev, ...(result.dependencyPreviews || {}) }));
      loadDependencyPreviews();
    } catch (err) {
      setError('Compile failed: ' + err.message);
    } finally {
      setCompiling(false);
    }
  };

  // ─── Upload ───────────────────────────────────────────────────────────────

  const handleUpload = async (fileType, file, uploadPath) => {
    setUploading((s) => ({ ...s, [fileType]: true }));
    setUploadResults((s) => ({ ...s, [fileType]: null }));
    try {
      const intakeId = intake?.id;
      if (!intakeId) throw new Error('No intake found. Create the client first.');
      const result = await apiUpload(
        uploadPath || `/intake/v2/${intakeId}/upload`,
        file,
        { file_type: fileType }
      );

      // GL uploads run as a background job (avoids Azure's ~230s edge timeout).
      // The POST returns immediately with a job id; poll until it finishes so
      // the wizard stays hard-gated until GL rows are synced into gl_entries.
      if (fileType === 'gl' && result?.status === 'accepted' && result?.job_id) {
        await pollGlJob(intakeId, result.job_id, file.name);
        return; // pollGlJob owns uploadResults / loadIntake on completion
      }

      // Schedule H/I/K rules extraction also runs as a background job (the AI
      // call generates up to ~32K tokens and would exceed Azure's ~230s edge
      // timeout in-request). Poll until it finishes, then compile.
      const scheduleRulesTypes = ['schedule_h_rules', 'schedule_i_rules', 'schedule_k_rules'];
      if (scheduleRulesTypes.includes(fileType) && result?.status === 'accepted' && result?.schedule_letter) {
        await pollScheduleRulesJob(intakeId, result.schedule_letter, fileType, file.name);
        return; // pollScheduleRulesJob owns uploadResults / loadIntake / compile
      }

      setUploadResults((s) => ({ ...s, [fileType]: { success: `Uploaded: ${file.name}`, data: result } }));
      await loadIntake();
      const autoCompileTypes = ['rules', 'schedule_h_rules', 'schedule_i_rules', 'schedule_k_rules', 'schedule_g_listing', 'schedule_l_listing', 'schedule_l_taxpayroll'];
      if (autoCompileTypes.includes(fileType)) {
        await handleCompile();
      }
    } catch (err) {
      setUploadResults((s) => ({ ...s, [fileType]: { error: err.message } }));
    } finally {
      setUploading((s) => ({ ...s, [fileType]: false }));
    }
  };

  // Poll the async GL upload job to completion, surfacing granular progress.
  const pollGlJob = async (intakeId, jobId, fileName) => {
    const STATUS_LABEL = {
      queued: 'Queued…',
      storing: 'Storing GL file…',
      scanning: 'Scanning GL structure…',
      syncing: 'Ingesting GL rows into queryable storage…',
    };
    const TERMINAL = new Set(['completed', 'error']);
    // ~10 min ceiling at 2s cadence — far beyond a normal large GL sync.
    const MAX_POLLS = 300;
    setGlJob({ status: 'queued', label: STATUS_LABEL.queued });
    try {
      for (let i = 0; i < MAX_POLLS; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        let job;
        try {
          // eslint-disable-next-line no-await-in-loop
          job = await api(`/intake/v2/${intakeId}/gl-upload-status?job_id=${encodeURIComponent(jobId)}`);
        } catch (pollErr) {
          // Transient poll failure — keep trying until the ceiling.
          continue;
        }
        setGlJob({ status: job.status, label: STATUS_LABEL[job.status] || job.status, job });
        if (TERMINAL.has(job.status)) {
          if (job.status === 'completed') {
            const glSync = job.result?.gl_sync || null;
            setUploadResults((s) => ({
              ...s,
              gl: { success: `Uploaded: ${fileName}`, data: { gl_sync: glSync, file_id: job.file_id } },
            }));
          } else {
            setUploadResults((s) => ({
              ...s,
              gl: { error: job.error || 'GL import failed. The file is stored — you can retry.' },
            }));
          }
          // eslint-disable-next-line no-await-in-loop
          await loadIntake(); // picks up gl_import_status + gl_extraction
          return job;
        }
      }
      setUploadResults((s) => ({
        ...s,
        gl: { error: 'GL import is taking unusually long. It may still finish — refresh to check.' },
      }));
    } finally {
      setGlJob((prev) => (prev && !['completed', 'error'].includes(prev.status) ? null : prev));
    }
  };

  // Poll the async Schedule H/I/K rules extraction to completion. handleUpload
  // keeps `uploading[fileType]` true across this await, so the step shows a
  // spinner until the AI extraction lands.
  const pollScheduleRulesJob = async (intakeId, letter, fileType, fileName) => {
    const TERMINAL = new Set(['completed', 'error', 'stale']);
    const MAX_POLLS = 300; // ~10 min at 2s cadence
    for (let i = 0; i < MAX_POLLS; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 2000));
      let res;
      try {
        // eslint-disable-next-line no-await-in-loop
        res = await api(`/intake/v2/${intakeId}/schedule-rules-status/${letter}`);
      } catch (pollErr) {
        continue; // transient — keep polling
      }
      if (TERMINAL.has(res.status)) {
        if (res.status === 'completed') {
          setUploadResults((s) => ({
            ...s,
            [fileType]: { success: `Extracted ${res.rule_count} rule${res.rule_count === 1 ? '' : 's'} from ${fileName}`, data: res },
          }));
        } else {
          const warn = res.extraction?.warnings?.[0]
            || 'AI extraction did not complete. The file is stored — you can retry the upload.';
          setUploadResults((s) => ({ ...s, [fileType]: { error: warn } }));
        }
        // eslint-disable-next-line no-await-in-loop
        await loadIntake();      // picks up schedule_*_rules_extraction + status
        // eslint-disable-next-line no-await-in-loop
        await handleCompile();
        return res;
      }
    }
    setUploadResults((s) => ({
      ...s,
      [fileType]: { error: 'Rules extraction is taking unusually long. It may still finish — refresh to check.' },
    }));
    return null;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api('/compiled-onboarding/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId }),
      });
      if (res && res.saved === false) {
        // Backend refused to replace an existing draft — section edits are
        // already persisted as you go, so this is informational, not success.
        alert(res.warning || 'A draft configuration already exists — your section edits are saved automatically as you make them.');
      } else {
        alert('Configuration saved successfully!');
      }
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Activate ─────────────────────────────────────────────────────────────

  const handleActivate = async () => {
    if (!window.confirm('Activate this client configuration? This will promote it to active status.')) return;
    setActivating(true);
    setError('');
    try {
      // Single entry point: the backend promotes the compiled draft config
      // server-side before running the data syncs, so activation behaves the
      // same here and on the Client Management page.
      const result = await api(`/clients/${clientId}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const parity = result?.parity_check;
      if (parity?.checked && !parity?.passed) {
        // Activation succeeded, but production numbers diverge from the
        // wizard's validated calcResults. Stay on this screen so the
        // analyst sees the per-section mismatches before moving on.
        setActivationDone(true);
        setParityWarning(parity);
      } else {
        alert('Client activated successfully!');
        onCancel();
      }
    } catch (err) {
      setError('Activation failed: ' + err.message);
    } finally {
      setActivating(false);
    }
  };

  // ─── Revalidate (Gap 4) ───────────────────────────────────────────────────

  const handleRevalidate = async (sectionId) => {
    setRevalidating((s) => ({ ...s, [sectionId]: true }));
    setError('');
    try {
      await api(`/compiled-onboarding/${clientId}/revalidate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionId }),
      });
      await handleCompile();
    } catch (err) {
      setError('Revalidation failed: ' + err.message);
    } finally {
      setRevalidating((s) => ({ ...s, [sectionId]: false }));
    }
  };

  // ─── Clone (Gap 6) ────────────────────────────────────────────────────────

  const handleClone = async () => {
    if (!window.confirm('Clone from the active configuration? This will overwrite the current draft.')) return;
    setCloning(true);
    setError('');
    try {
      await api(`/compiled-onboarding/${clientId}/clone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ forceOverwrite: true }),
      });
      alert('Configuration cloned from active successfully!');
      await loadVersionHistory();
      await loadIntake();
    } catch (err) {
      setError('Clone failed: ' + err.message);
    } finally {
      setCloning(false);
    }
  };

  // ─── Auto-save enabled modules ────────────────────────────────────────────

  const modulesInitialized = useRef(false);
  useEffect(() => {
    if (!modulesInitialized.current) { modulesInitialized.current = true; return; }
    if (!intake?.id) return;
    const timer = setTimeout(async () => {
      try {
        await api(`/intake/v2/${intake.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled_modules: enabledModules }),
        });
      } catch (err) {
        console.warn('Could not save modules:', err.message);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [enabledModules, intake?.id]);

  // ─── Auto-save client profile ─────────────────────────────────────────────

  const profileInitialized = useRef(false);
  useEffect(() => {
    if (!profileInitialized.current) { profileInitialized.current = true; return; }
    if (!intake?.id) return;
    const timer = setTimeout(async () => {
      try {
        await api(`/intake/v2/${intake.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            client_name: clientName,
            fiscal_year: fiscalYear,
            notes,
            reporting_period: reportingPeriod,
            source_system: sourceSystem,
            version_label: versionLabel,
            // Per-section counts drive the wizard's dynamic step list.
            fringe_pool_count: fringeCount,
            allocation_count: allocationCount,
            oh_division_count: overheadCount,
            has_ga: hasGA,
            include_material_handling: includeMH,
            section_names: sectionNames,
          }),
        });
      } catch (err) {
        console.warn('Could not save client profile:', err.message);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [clientName, fiscalYear, notes, reportingPeriod, sourceSystem, versionLabel, fringeCount, allocationCount, overheadCount, hasGA, includeMH, sectionNames, intake?.id]);

  // ─── Derived values ───────────────────────────────────────────────────────

  const staleSections = sectionStatuses.filter((s) => s.status === 'STALE');
  const hasBlockingStale = staleSections.length > 0;

  const isOptionalDisabled = (stepId) => {
    if (stepId === 'schedule_i_template' || stepId === 'schedule_i_rules') return !enabledModules.schedule_i;
    if (stepId === 'schedule_k_template' || stepId === 'schedule_k_rules') return !enabledModules.schedule_k;
    return false;
  };

  // ─── Screen 1: Client Setup ───────────────────────────────────────────────

  const renderClientSetup = () => (
    <div>
      <h2 className="text-xl font-bold mb-4">Client Setup</h2>
      <p className="text-gray-600 mb-6">
        Configure the client profile and select which onboarding modules are required.
      </p>

      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <p className="text-sm font-medium mb-1">
          Client ID: <span className="font-mono">{clientId}</span>
        </p>
        {intake && (
          <p className="text-sm">
            Intake: <span className="font-mono">{intake.id}</span> — Status: {intake.status}
          </p>
        )}
      </div>

      <div className="space-y-4 mb-6">
        {/* Client Name is set when the client record was created — display it
            read-only here instead of re-asking. */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
          <div className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
            {clientName || <span className="text-gray-400 italic">unnamed client</span>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fiscal Year</label>
          <input
            type="text"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            placeholder="2024"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional notes about this client's configuration..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-1">Section Counts</h3>
        <p className="text-xs text-gray-500 mb-3">
          How many of each section type does this client have? Each count generates a separate
          step in the wizard where you can name and upload that section's source.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Fringe Sections', value: fringeCount, set: setFringeCount, max: 20 },
            { label: 'Allocation Sections', value: allocationCount, set: setAllocationCount, max: 50 },
            { label: 'Overhead Sections', value: overheadCount, set: setOverheadCount, max: 20 },
          ].map(({ label, value, set, max }) => (
            <div key={label} className="p-3 bg-white border rounded-lg">
              <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
              <input
                type="number"
                min={0}
                max={max}
                value={value}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  set(Number.isFinite(n) && n >= 0 ? Math.min(n, max) : 0);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
          <label className="flex items-center gap-2 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={hasGA}
              onChange={() => setHasGA((v) => !v)}
              className="rounded"
            />
            <span className="text-sm">Has G&A pool</span>
          </label>
          <label className="flex items-center gap-2 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={includeMH}
              onChange={() => setIncludeMH((v) => !v)}
              className="rounded"
            />
            <span className="text-sm">Has M&H pool</span>
          </label>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Other Modules</h3>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(enabledModules).map(([key, val]) => (
            <label
              key={key}
              className="flex items-center gap-2 p-3 bg-white border rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={val}
                onChange={() => setEnabledModules((s) => ({ ...s, [key]: !s[key] }))}
                className="rounded"
              />
              <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Screen 2: GL Upload ──────────────────────────────────────────────────

  const renderGLUpload = () => (
    <div>
      <h2 className="text-xl font-bold mb-4">GL Upload</h2>
      <p className="text-gray-600 mb-6">
        Upload the source General Ledger file. Year context comes from the Fiscal Year set in Client Setup.
      </p>

      <FileUploadBox
        label="General Ledger File (.xlsx)"
        onUpload={(file) => handleUpload('gl', file)}
        uploading={uploading.gl}
        result={uploadResults.gl}
        existing={intake?.gl_file_id ? 'GL file uploaded' : null}
      />

      {glImporting && (
        <div className="mt-3 p-3 rounded-lg border bg-blue-50 border-blue-200 flex items-center gap-2">
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
          <div>
            <p className="text-sm font-medium text-blue-800">
              {glJob?.label || 'Processing GL…'}
            </p>
            <p className="text-xs text-blue-700 mt-0.5">
              Large GL files are imported in the background. Rate calculations stay locked until the GL rows finish syncing.
            </p>
          </div>
        </div>
      )}

      {uploadResults.gl?.data?.gl_sync && (
        (() => {
          const s = uploadResults.gl.data.gl_sync;
          const ok = s.synced && (s.row_count || 0) > 0;
          return (
            <div className={`mt-3 p-3 rounded-lg border ${ok ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
              <p className={`text-sm font-medium ${ok ? 'text-green-800' : 'text-yellow-800'}`}>
                {ok
                  ? `Ingested ${s.row_count.toLocaleString()} GL rows into queryable storage (gl_id: ${s.gl_id})`
                  : `GL ingestion did not complete — ${s.reason || s.error || 'unknown reason'}`}
              </p>
              {s.sheet && <p className="text-xs text-gray-600 mt-1">Sheet: <code>{s.sheet}</code> · Source: <code>{s.extraction_source}</code></p>}
              {!ok && (
                <p className="text-xs text-yellow-700 mt-1">
                  Rate calculations later in the wizard need ingested GL rows. Re-upload or check the GL file format.
                </p>
              )}
            </div>
          );
        })()
      )}

      {intake?.gl_extraction && (
        <div className="mt-4">
          {intake.gl_extraction.sample_rows?.length > 0 && (
            <div className="mb-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Sample Rows</h3>
              <div className="overflow-x-auto rounded border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {Object.keys(intake.gl_extraction.sample_rows[0] || {}).map((col) => (
                        <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 border-b whitespace-nowrap">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {intake.gl_extraction.sample_rows.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-b last:border-0">
                        {Object.values(row).map((val, j) => (
                          <td key={j} className="px-3 py-2 text-gray-700 whitespace-nowrap">{String(val ?? '')}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <ExtractionPreview title="GL Structure Extraction" data={intake.gl_extraction} />
        </div>
      )}
    </div>
  );

  // ─── Screen 3: GL Mapping Upload ──────────────────────────────────────────

  const renderGLMapping = () => {
    const glm = intake?.gl_mapping_extraction;
    const glSchema = glm?.gl_schema;
    const fieldMappings = glSchema?.fieldMappings || {};
    const structures = glSchema?.structures || {};

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">GL Mapping Upload</h2>
        <p className="text-gray-600 mb-6">
          Define how the GL should be interpreted — canonical field bindings, account structure, org structure, and segment levels.
        </p>

        <FileUploadBox
          label="GL Mapping File (.xlsx)"
          onUpload={(file) => handleUpload('gl_mapping', file)}
          uploading={uploading.gl_mapping}
          result={uploadResults.gl_mapping}
          existing={intake?.gl_mapping_file_id ? 'GL mapping file uploaded' : null}
        />

        {glm && (
          <div className="mt-4">
            {glm.valid === false && glm.errors?.length > 0 && (
              <div className="p-3 bg-red-50 rounded-lg mb-4">
                <p className="text-sm font-medium text-red-800 mb-1">Parse Errors</p>
                {glm.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-700">{e}</p>
                ))}
              </div>
            )}

            {glm.warnings?.length > 0 && (
              <div className="p-3 bg-yellow-50 rounded-lg mb-4">
                <p className="text-sm font-medium text-yellow-800 mb-1">Warnings</p>
                {glm.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-yellow-700">{w}</p>
                ))}
              </div>
            )}

            {Object.keys(fieldMappings).length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Field Mappings</h3>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Canonical Field</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 border-b">Source Column</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(fieldMappings).map(([canonical, source]) => (
                        <tr key={canonical} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-blue-700">{canonical}</td>
                          <td className="px-3 py-2 font-mono text-gray-700">{source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {Object.keys(structures).length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">ID Structures</h3>
                {Object.entries(structures).map(([name, struct]) => (
                  <div key={name} className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-800 mb-1">
                      {name} <span className="text-gray-500 font-normal">(delimiter: {struct.delimiter || '—'})</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(struct.levels || []).map((lvl, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          L{lvl.position}: {lvl.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 4: Section Plan ───────────────────────────────────────────────

  const renderSectionPlan = () => {
    const re = intake?.rules_extraction || {};
    const pools = re.pool_structure || [];
    const fringes = pools.filter((p) => p.type === 'fringe');
    const allocs = pools.filter((p) => p.type === 'allocation');
    const overheads = pools.filter((p) => p.type === 'overhead');
    const ga = pools.filter((p) => p.type === 'ga' || p.key === 'ga');

    const getTypeStatus = (keyFragment) => {
      const relevant = sectionStatuses.filter((s) => s.sectionId?.toLowerCase().includes(keyFragment));
      if (relevant.some((s) => s.status === 'ERROR')) return 'ERROR';
      if (relevant.some((s) => s.status === 'STALE')) return 'STALE';
      if (relevant.length > 0 && relevant.every((s) => s.status === 'VALID')) return 'VALID';
      return null;
    };

    const checklistItems = [
      { label: 'GL File', done: !!intake?.gl_file_id, statusKey: null },
      { label: 'GL Mapping File', done: !!(intake?.gl_mapping_file_id || intake?.mapping_file_id), statusKey: null },
      { label: 'Fringe Sections', done: fringes.length > 0, statusKey: 'fringe' },
      { label: 'Allocation Sections', done: allocs.length > 0, statusKey: 'alloc' },
      { label: 'Overhead Sections', done: overheads.length > 0, statusKey: 'overhead' },
      { label: 'G&A Section', done: ga.length > 0, statusKey: 'ga' },
      { label: 'Schedule H Template', done: !!intake?.schedule_h_extraction, statusKey: 'schedule_h' },
      { label: 'Schedule H Rules', done: !!(uploadResults.schedule_h_rules?.success || intake?.schedule_h_rules_file_id), statusKey: 'schedule_h' },
      { label: 'Schedule I Template', done: !!intake?.schedule_i_extraction, optional: !enabledModules.schedule_i, statusKey: 'schedule_i' },
      { label: 'Schedule I Rules', done: !!(uploadResults.schedule_i_rules?.success || intake?.schedule_i_rules_file_id), optional: !enabledModules.schedule_i, statusKey: 'schedule_i' },
      { label: 'Schedule K Template', done: !!intake?.schedule_k_template_file_id, optional: !enabledModules.schedule_k, statusKey: 'schedule_k' },
      { label: 'Schedule K Rules', done: !!(uploadResults.schedule_k_rules?.success || intake?.schedule_k_rules_file_id), optional: !enabledModules.schedule_k, statusKey: 'schedule_k' },
      { label: 'Adequacy Check Rules', done: !!intake?.adequacy_check_file_id, statusKey: null },
    ];

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Onboarding Section Plan</h2>
        <p className="text-gray-600 mb-6">
          Review required uploads and extracted pool structure. Sections are configured one-by-one via AI Extract in the next steps.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SectionBadge count={fringes.length} label="Fringes" present={fringes.length > 0} />
          <SectionBadge count={allocs.length} label="Allocations" present={allocs.length > 0} />
          <SectionBadge count={overheads.length} label="Overheads" present={overheads.length > 0} />
          <SectionBadge count={ga.length} label="G&A" present={ga.length > 0} />
        </div>

        <div className="mb-6">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Required Uploads Checklist</h3>
          <div className="space-y-2">
            {checklistItems.map(({ label, done, optional, statusKey }) => {
              const typeStatus = statusKey ? getTypeStatus(statusKey) : null;
              return (
                <div
                  key={label}
                  className={`flex items-center justify-between p-3 bg-white border rounded-lg ${optional ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    {done ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${done ? 'text-green-800 font-medium' : 'text-gray-600'}`}>
                      {label}{optional ? ' (optional)' : ''}
                    </span>
                  </div>
                  {typeStatus && <StatusBadge status={typeStatus} />}
                </div>
              );
            })}
          </div>
        </div>

        {pools.length > 0 && (
          <div>
            <h3 className="font-semibold text-sm text-gray-700 mb-3">Extracted Pool Structure</h3>
            <div className="space-y-2">
              {pools.map((p, i) => {
                const poolStatus = sectionStatuses.find((s) => s.sectionId === p.key);
                return (
                  <div key={i} className="flex items-center justify-between p-2 bg-white border rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium">{p.key}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        p.type === 'fringe' ? 'bg-cyan-100 text-cyan-800' :
                        p.type === 'allocation' ? 'bg-blue-100 text-blue-800' :
                        p.type === 'overhead' ? 'bg-amber-100 text-amber-800' :
                        p.type === 'ga' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>{p.type}</span>
                    </div>
                    {poolStatus && <StatusBadge status={poolStatus.status} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Generic per-section step renderer ──────────────────────────────────
  // Each step in Section Configuration is dynamic — keyed by sectionId
  // (fringe_1, allocation_3, etc.). Renders a name input + AIExtractBox +
  // a compiled-definition preview filtered to this section.
  const renderSection = (step) => {
    if (!step) return null;
    const { id: sectionId, sectionType, defaultLabel } = step;
    const persistedName = sectionNames[sectionId] || '';
    const config = compiledConfig?.config;

    let definition = null;
    if (sectionType === 'fringe') {
      definition = (config?.fringeDefinitions || []).find((d) => d.fringeId === sectionId);
    } else if (sectionType === 'allocation') {
      definition = (config?.allocationDefinitions || []).find((d) => d.allocationId === sectionId);
    } else if (sectionType === 'overhead' || sectionType === 'material_handling') {
      // M&H compiles into an overhead-typed definition (type='material_handling'),
      // so it lives in overheadDefinitions keyed by its own section id.
      definition = (config?.overheadDefinitions || []).find((d) => d.overheadId === sectionId);
    } else if (sectionType === 'ga') {
      definition = config?.gaDefinition || null;
    }

    const sectionStatus = definition && sectionStatuses.find(
      (s) => s.sectionId === (definition.fringeId || definition.allocationId || definition.overheadId || definition.gaId)
    );
    const preview = definition && (
      dependencyPreviews[definition.fringeId || definition.allocationId || definition.overheadId || definition.gaId]
      || dependencyPreviews[sectionId]
    );

    const sectionTypeLabel = SECTION_TYPE_LABELS[sectionType] || (sectionType[0].toUpperCase() + sectionType.slice(1));
    const headingDescription = {
      fringe: 'Use AI Extract on a workbook tab. Each fringe pool is built independently with pool accounts, base accounts, filters, and a calculated rate.',
      allocation: 'Use AI Extract on a workbook tab. Each allocation shows pool accounts, basis type, receiver groups, and upstream dependencies.',
      overhead: 'Use AI Extract on a workbook tab. Each overhead pool shows pool accounts, base accounts, modifiers, and a calculated rate.',
      ga: 'Use AI Extract on a workbook tab. G&A is configured after all prerequisite structures (fringe, allocations, overhead) are available.',
      material_handling: 'Use AI Extract on a workbook tab. The Material & Handling pool shows pool accounts, its base (materials/subcontracts it burdens), modifiers, and a calculated rate.',
    }[sectionType];

    return (
      <div>
        <h2 className="text-xl font-bold mb-1">{step.label || defaultLabel}</h2>
        <p className="text-gray-600 mb-4">{headingDescription}</p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Section Name <span className="text-gray-400 font-normal">— shown in the sidebar and saved with the proposal</span>
          </label>
          <input
            type="text"
            value={persistedName}
            placeholder={defaultLabel}
            onChange={(e) => updateSectionName(sectionId, e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <AIExtractBox
          key={sectionId}
          clientId={clientId}
          sectionType={sectionType}
          sectionId={sectionId}
          sectionName={persistedName || defaultLabel}
          state={sectionAIState[sectionId]}
          onChange={(updates) => updateSectionAI(sectionId, updates)}
          glImporting={glImporting}
        />

        {definition ? (
          <div className="p-4 bg-white border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="font-mono font-semibold text-sm">{definition.fringeId || definition.allocationId || definition.overheadId || definition.gaId}</span>
                {definition.name && <span className="ml-2 text-sm text-gray-600">{definition.name}</span>}
              </div>
              {sectionStatus && <StatusBadge status={sectionStatus.status} />}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-700">Pool Accounts:</span>{' '}
                {definition.poolAccounts?.length ? definition.poolAccounts.join(', ') : '—'}
              </div>
              {sectionType === 'allocation' ? (
                <div>
                  <span className="font-medium text-gray-700">Basis:</span>{' '}
                  <span className="font-mono">{definition.basis?.basisType || '—'}</span>
                </div>
              ) : (
                <div>
                  <span className="font-medium text-gray-700">Base Accounts:</span>{' '}
                  {definition.baseAccounts?.length ? definition.baseAccounts.join(', ') : '—'}
                </div>
              )}
              <div>
                <span className="font-medium text-gray-700">Filters:</span>{' '}
                {(definition.filters?.length || definition.poolFilters?.length || 0)} filter(s)
              </div>
              <div>
                <span className="font-medium text-gray-700">{sectionType === 'allocation' ? 'Receivers' : 'Modifiers'}:</span>{' '}
                {sectionType === 'allocation'
                  ? `${definition.basis?.receiverGroups?.length || 0} receiver(s)`
                  : `${definition.poolModifiers?.length || 0} modifier(s)`}
              </div>
            </div>
            {definition.sourceArtifact && (
              <div className="mt-2 text-xs text-gray-500">
                Source: {definition.sourceArtifact.sourceName || definition.sourceArtifact.artifactType || '—'}
              </div>
            )}
            <DependencyPanel preview={preview} />
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-lg text-sm">
            No {sectionTypeLabel.toLowerCase()} definition compiled yet for this section. Use AI Extract above, then compile.
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 5: Fringe Uploads ─────────────────────────────────────────────

  const renderFringeUploads = () => {
    const fringes = compiledConfig?.config?.fringeDefinitions || [];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Fringe Uploads</h2>
        <p className="text-gray-600 mb-6">
          Use AI Extract on a workbook tab. Each fringe pool is built independently with pool accounts, base accounts, filters, and calculated rate.
        </p>

        <AIExtractBox
          key="fringe"
          clientId={clientId}
          sectionType="fringe"
          state={sectionAIState.fringe}
          onChange={(updates) => updateSectionAI('fringe', updates)}
          glImporting={glImporting}
        />

        {fringes.length > 0 ? (
          <div className="space-y-4">
            {fringes.map((f, i) => {
              const sectionStatus = sectionStatuses.find((s) => s.sectionId === f.fringeId);
              const preview = dependencyPreviews[f.fringeId];
              return (
                <div key={i} className="p-4 bg-white border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-mono font-semibold text-sm">{f.fringeId}</span>
                      {f.name && <span className="ml-2 text-sm text-gray-600">{f.name}</span>}
                    </div>
                    {sectionStatus && <StatusBadge status={sectionStatus.status} />}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div>
                      <span className="font-medium text-gray-700">Pool Accounts:</span>{' '}
                      {f.poolAccounts?.length ? f.poolAccounts.join(', ') : '—'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Base Accounts:</span>{' '}
                      {f.baseAccounts?.length ? f.baseAccounts.join(', ') : '—'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Filters:</span>{' '}
                      {f.filters?.length || 0} filter(s)
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Formula:</span>{' '}
                      <span className="font-mono">{f.formula || '—'}</span>
                    </div>
                  </div>
                  {/* Calculated rate output */}
                  {f.outputs && Object.keys(f.outputs).length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Calculated Output</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(f.outputs).map(([key, val]) => (
                          <div key={key}>
                            <span className="text-blue-600 font-medium">{key}:</span>{' '}
                            <span className="font-mono">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {f.sourceArtifact && (
                    <div className="mt-2 text-xs text-gray-500">
                      Source: {f.sourceArtifact.sourceName || f.sourceArtifact.artifactType || 'rules workbook'}
                    </div>
                  )}
                  <DependencyPanel preview={preview} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No fringe definitions compiled yet. Use AI Extract above to propose a fringe definition from a workbook tab.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 6: Allocation Uploads ────────────────────────────────────────

  const renderAllocationUploads = () => {
    const allocs = compiledConfig?.config?.allocationDefinitions || [];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Allocation Uploads</h2>
        <p className="text-gray-600 mb-6">
          Use AI Extract on a workbook tab. Each allocation shows pool accounts, basis type, receiver groups, and upstream dependencies.
        </p>

        <AIExtractBox
          key="allocation"
          clientId={clientId}
          sectionType="allocation"
          state={sectionAIState.allocation}
          onChange={(updates) => updateSectionAI('allocation', updates)}
          glImporting={glImporting}
        />

        {allocs.length > 0 ? (
          <div className="space-y-4">
            {allocs.map((a, i) => {
              const sectionStatus = sectionStatuses.find((s) => s.sectionId === a.allocationId);
              const preview = dependencyPreviews[a.allocationId];
              return (
                <div key={i} className="p-4 bg-white border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-mono font-semibold text-sm">{a.allocationId}</span>
                      {a.name && <span className="ml-2 text-sm text-gray-600">{a.name}</span>}
                    </div>
                    {sectionStatus && <StatusBadge status={sectionStatus.status} />}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div>
                      <span className="font-medium text-gray-700">Pool Accounts:</span>{' '}
                      {a.poolAccounts?.length ? a.poolAccounts.join(', ') : '—'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Basis:</span>{' '}
                      <span className="font-mono">{a.basis?.basisType || a.basisType || '—'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Pool Filters:</span>{' '}
                      {a.poolFilters?.length || 0} filter(s)
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Modifiers:</span>{' '}
                      {a.poolModifiers?.length || 0} modifier(s)
                    </div>
                  </div>
                  {/* Receiver groups */}
                  {a.basis?.receiverGroups?.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Receiver Groups</p>
                      <div className="space-y-1 text-xs">
                        {a.basis.receiverGroups.map((rg, j) => (
                          <div key={j} className="flex items-center justify-between">
                            <span className="font-medium">{rg.name || rg.receiverId || `Group ${j + 1}`}</span>
                            {rg.percentage != null && <span className="font-mono">{rg.percentage}%</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Allocation output preview */}
                  {a.outputs && Object.keys(a.outputs).length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs font-semibold text-green-700 uppercase mb-2">Allocation Output</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(a.outputs).map(([key, val]) => (
                          <div key={key}>
                            <span className="text-green-600 font-medium">{key}:</span>{' '}
                            <span className="font-mono">{String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.sourceArtifact && (
                    <div className="mt-2 text-xs text-gray-500">
                      Source: {a.sourceArtifact.sourceName || a.sourceArtifact.artifactType || 'rules workbook'}
                    </div>
                  )}
                  <DependencyPanel preview={preview} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No allocation definitions compiled yet. Use AI Extract above to propose an allocation definition from a workbook tab.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 7: Overhead Uploads ───────────────────────────────────────────

  const renderOverheadUploads = () => {
    const overheads = compiledConfig?.config?.overheadDefinitions || [];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Overhead Uploads</h2>
        <p className="text-gray-600 mb-6">
          Use AI Extract on a workbook tab. Each pool shows pool accounts, base accounts, upstream dependencies, and calculated overhead rate.
        </p>

        <AIExtractBox
          key="overhead"
          clientId={clientId}
          sectionType="overhead"
          state={sectionAIState.overhead}
          onChange={(updates) => updateSectionAI('overhead', updates)}
          glImporting={glImporting}
        />

        {overheads.length > 0 ? (
          <div className="space-y-4">
            {overheads.map((o, i) => {
              const sectionStatus = sectionStatuses.find((s) => s.sectionId === o.overheadId);
              const preview = dependencyPreviews[o.overheadId];
              return (
                <div key={i} className="p-4 bg-white border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-mono font-semibold text-sm">{o.overheadId}</span>
                      {o.name && <span className="ml-2 text-sm text-gray-600">{o.name}</span>}
                    </div>
                    {sectionStatus && <StatusBadge status={sectionStatus.status} />}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                    <div>
                      <span className="font-medium text-gray-700">Pool Accounts:</span>{' '}
                      {o.poolAccounts?.length ? o.poolAccounts.join(', ') : '—'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Base Accounts:</span>{' '}
                      {o.baseAccounts?.length ? o.baseAccounts.join(', ') : '—'}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Filters:</span>{' '}
                      {o.filters?.length || 0} filter(s)
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Modifiers:</span>{' '}
                      {o.poolModifiers?.length || 0} modifier(s)
                    </div>
                  </div>
                  {/* Calculated rate output */}
                  {o.outputs && Object.keys(o.outputs).length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Calculated Output</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(o.outputs).map(([key, val]) => (
                          <div key={key}>
                            <span className="text-blue-600 font-medium">{key}:</span>{' '}
                            <span className="font-mono">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {o.sourceArtifact && (
                    <div className="mt-2 text-xs text-gray-500">
                      Source: {o.sourceArtifact.sourceName || o.sourceArtifact.artifactType || 'rules workbook'}
                    </div>
                  )}
                  <DependencyPanel preview={preview} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No overhead definitions compiled yet. Use AI Extract above to propose an overhead definition from a workbook tab.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 8: G&A Upload ─────────────────────────────────────────────────

  const renderGAUpload = () => {
    const ga = compiledConfig?.config?.gaDefinition;
    const gaStatus = ga ? sectionStatuses.find((s) => s.sectionId === ga.gaId || s.sectionId?.includes('ga')) : null;
    const gaPreview = ga ? (dependencyPreviews[ga.gaId] || dependencyPreviews['ga']) : null;

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">G&A Upload</h2>
        <p className="text-gray-600 mb-6">
          Use AI Extract on a workbook tab. G&A is configured after all prerequisite structures (fringe, allocations, overhead) are available.
        </p>

        <AIExtractBox
          key="ga"
          clientId={clientId}
          sectionType="ga"
          state={sectionAIState.ga}
          onChange={(updates) => updateSectionAI('ga', updates)}
          glImporting={glImporting}
        />

        {ga ? (
          <div className="p-4 bg-white border rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="font-mono font-semibold text-sm">{ga.gaId}</span>
                {ga.name && <span className="ml-2 text-sm text-gray-600">{ga.name}</span>}
              </div>
              {gaStatus && <StatusBadge status={gaStatus.status} />}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
              <div>
                <span className="font-medium text-gray-700">Pool Accounts:</span>{' '}
                {ga.poolAccounts?.length ? ga.poolAccounts.join(', ') : '—'}
              </div>
              <div>
                <span className="font-medium text-gray-700">Base Accounts:</span>{' '}
                {ga.baseAccounts?.length ? ga.baseAccounts.join(', ') : '—'}
              </div>
              <div>
                <span className="font-medium text-gray-700">Modifiers:</span>{' '}
                {ga.poolModifiers?.length || 0} modifier(s)
              </div>
              <div>
                <span className="font-medium text-gray-700">Basis Type:</span>{' '}
                <span className="font-mono">{ga.basisType || '—'}</span>
              </div>
            </div>
            {/* Receiver logic */}
            {ga.receiverLogic?.length > 0 && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs font-semibold text-blue-700 uppercase mb-2">Receiver Logic</p>
                <div className="space-y-1 text-xs">
                  {ga.receiverLogic.map((rl, j) => (
                    <div key={j} className="flex items-center justify-between">
                      <span className="font-medium">{rl.name || rl.receiverId || `Receiver ${j + 1}`}</span>
                      {rl.filter && <span className="text-gray-500 font-mono">{JSON.stringify(rl.filter)}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Calculated G&A output */}
            {ga.outputs && Object.keys(ga.outputs).length > 0 && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs font-semibold text-green-700 uppercase mb-2">Calculated Output</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(ga.outputs).map(([key, val]) => (
                    <div key={key}>
                      <span className="text-green-600 font-medium">{key}:</span>{' '}
                      <span className="font-mono">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {ga.sourceArtifact && (
              <div className="mt-2 text-xs text-gray-500">
                Source: {ga.sourceArtifact.sourceName || ga.sourceArtifact.artifactType || 'rules workbook'}
              </div>
            )}
            <DependencyPanel preview={gaPreview} />
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No G&A definition compiled yet. Use AI Extract above to propose a G&A definition from a workbook tab.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 9: Schedule H Template ───────────────────────────────────────

  const renderScheduleHTemplate = () => (
    <div>
      <h2 className="text-xl font-bold mb-4">Schedule H Template Upload</h2>
      <p className="text-gray-600 mb-6">
        Upload the client-specific Schedule H layout. The system will detect columns, rows, and division labels.
      </p>

      <FileUploadBox
        label="Schedule H Template (.xlsx)"
        onUpload={(file) => handleUpload('schedule_h_template', file)}
        uploading={uploading.schedule_h_template}
        result={uploadResults.schedule_h_template}
        existing={intake?.schedule_h_extraction ? 'Schedule H template uploaded' : null}
      />

      {intake?.schedule_h_extraction && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Layout Preview</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_h_extraction.columns?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Columns Detected</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_h_extraction.columns?.filter((c) => c.division_number).length || 0}
              </p>
              <p className="text-xs text-blue-700">With Division Data</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_h_extraction.rows?.length || intake.schedule_h_extraction.layout?.rows?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Rows Detected</p>
            </div>
          </div>
          <ExtractionPreview title="Schedule H Extraction" data={intake.schedule_h_extraction} />
        </div>
      )}
    </div>
  );

  // ─── Schedule G Listing (listing schedule — no calculation rules) ────────
  const renderScheduleGListing = () => {
    const ext = intake?.schedule_g_extraction;
    const compiledG = compiledConfig?.config?.scheduleDefinitions?.schedule_g;
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Schedule G — Account Listing</h2>
        <p className="text-gray-600 mb-6">
          Schedule G (Reconciliation of Books of Account and Claimed Direct Costs) is a
          listing schedule — no calculations. Upload the list of GL accounts to display.
          Subtotals break at each change in <strong>Account Level 1</strong> (the
          “Group” column). Amounts are pulled from the GL; “Adjustments”/“Notes” are
          adjusted later.
        </p>

        <button
          type="button"
          onClick={() => downloadTemplate('/schedule-g/template', 'Schedule_G_Listing_Template.xlsx')}
          className="flex items-center gap-2 mb-4 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
        >
          <Download className="w-4 h-4" /> Download Schedule G template
        </button>

        <FileUploadBox
          label="Schedule G Account Listing (.xlsx)"
          accept=".xlsx,.xls"
          onUpload={(file) => handleUpload('schedule_g_listing', file)}
          uploading={uploading.schedule_g_listing}
          result={uploadResults.schedule_g_listing}
          existing={ext ? `${ext.total_rows || 0} accounts uploaded` : null}
        />

        {ext?.warnings?.length > 0 && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            <ul className="list-disc ml-5">{ext.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}

        {compiledG && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">Schedule G compiled</span>
            </div>
            <div className="text-xs text-green-700">
              {compiledG.rules?.length || 0} account line(s) ·{' '}
              {new Set((compiledG.rules || []).map((r) => r.displayColumnLabel || 'Ungrouped')).size} subtotal group(s)
            </div>
          </div>
        )}

        {ext && <div className="mt-4"><ExtractionPreview title="Schedule G Listing" data={ext} /></div>}
      </div>
    );
  };

  // ─── Schedule L Listing (top GL section + tax/payroll block) ─────────────
  const renderScheduleLListing = () => {
    const ext = intake?.schedule_l_extraction;
    const tpExt = intake?.schedule_l_taxpayroll_extraction;
    const compiledL = compiledConfig?.config?.scheduleDefinitions?.schedule_l;
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Schedule L — Account Listing + Tax/Payroll</h2>
        <p className="text-gray-600 mb-6">
          Schedule L (Reconciliation of Total Payroll to Total Labor Distribution) has two
          parts. <strong>(1)</strong> The top section is a listing of GL labor accounts
          (amounts from the GL). <strong>(2)</strong> The bottom section is the tax/payroll
          reconciliation — values entered from the IRS&nbsp;941 and ADP payroll reports
          (not from the GL). Upload one file for each.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => downloadTemplate('/schedule-l/template/listing', 'Schedule_L_Listing_Template.xlsx')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
          >
            <Download className="w-4 h-4" /> Download listing template
          </button>
          <button
            type="button"
            onClick={() => downloadTemplate('/schedule-l/template/taxpayroll', 'Schedule_L_Tax-Payroll_Template.xlsx')}
            className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
          >
            <Download className="w-4 h-4" /> Download tax/payroll template
          </button>
        </div>

        <div className="mb-2 text-sm font-semibold text-gray-700">1 · GL Labor Account Listing</div>
        <FileUploadBox
          label="Schedule L Account Listing (.xlsx)"
          accept=".xlsx,.xls"
          onUpload={(file) => handleUpload('schedule_l_listing', file)}
          uploading={uploading.schedule_l_listing}
          result={uploadResults.schedule_l_listing}
          existing={ext ? `${ext.total_rows || 0} accounts uploaded` : null}
        />

        <div className="mt-6 mb-2 text-sm font-semibold text-gray-700">2 · Tax/Payroll Reconciliation (941 + ADP)</div>
        <FileUploadBox
          label="Schedule L Tax/Payroll Block (.xlsx)"
          accept=".xlsx,.xls"
          onUpload={(file) => handleUpload('schedule_l_taxpayroll', file)}
          uploading={uploading.schedule_l_taxpayroll}
          result={uploadResults.schedule_l_taxpayroll}
          existing={tpExt ? `${tpExt.total_rows || 0} tax/payroll lines uploaded` : null}
        />

        {(ext?.warnings?.length > 0 || tpExt?.warnings?.length > 0) && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            <ul className="list-disc ml-5">
              {(ext?.warnings || []).map((w, i) => <li key={`l${i}`}>{w}</li>)}
              {(tpExt?.warnings || []).map((w, i) => <li key={`tp${i}`}>{w}</li>)}
            </ul>
          </div>
        )}

        {compiledL && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">Schedule L compiled</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-green-700">
              <div>Labor lines: {(compiledL.rules || []).filter((r) => !String(r.rowId || '').startsWith('l_tp_')).length}</div>
              <div>Tax/payroll lines: {(compiledL.rules || []).filter((r) => String(r.rowId || '').startsWith('l_tp_')).length}</div>
            </div>
          </div>
        )}

        {ext && <div className="mt-4"><ExtractionPreview title="Schedule L Listing (top)" data={ext} /></div>}
        {tpExt && <div className="mt-4"><ExtractionPreview title="Schedule L Tax/Payroll (bottom)" data={tpExt} /></div>}
      </div>
    );
  };

  // ─── Screen 10: Schedule H Rules ─────────────────────────────────────────

  // Renders the AI-styled "extract rules with AI" box used for Schedule H/I/K
  // rules uploads. The backend's /upload route runs the LLM extractor on the
  // schedule_*_rules file_type, persists `schedule_*_rules_extraction` to the
  // intake, and the auto-compile then surfaces compiled rules. This box
  // mirrors AIExtractBox's blue styling for consistency.
  const renderScheduleRulesAIBox = ({ scheduleLetter, fileType, extraction, compiledSched, preview }) => {
    const letter = scheduleLetter.toUpperCase();
    const upper = letter;
    const lower = letter.toLowerCase();
    const isUploading = !!uploading[fileType];
    const result = uploadResults[fileType];
    const ruleCount = (extraction?.rules || []).length;
    const compiledRuleCount = compiledSched?.rules?.length || 0;
    const layoutRows = compiledSched?.layout?.rows?.length || 0;
    const sampleRules = (extraction?.rules || []).slice(0, 8);

    return (
      <>
        <div className="border-2 border-blue-200 bg-blue-50/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-sm">AI extract Schedule {upper} rules from workbook</h4>
            <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">claude-opus-4-7</span>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Drop the Schedule {upper} rules workbook (with prose like "sum accounts 500-100-001 and 500-100-002 where Org Level 4 is not 119").
            Claude will extract structured per-row rules (rule_type, source_accounts, filters, dependencies) and persist them to the intake.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={isUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(fileType, f);
              }}
              className="text-sm flex-1"
            />
            <span className="text-xs text-gray-500">
              {isUploading ? <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Extracting…</span> : 'Pick a file to extract'}
            </span>
          </div>
          {result?.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-2">
              {result.error}
            </div>
          )}
          {result?.success && intake?.[`schedule_${lower}_rules_file_id`] && (
            <div className="text-xs text-green-700 mb-2">Uploaded: <code className="bg-white px-1 rounded">{result.data?.filename}</code></div>
          )}

          {extraction && (
            <div className="bg-white border rounded p-3 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-gray-800">
                  AI extracted: <span className="text-blue-700">{ruleCount} rule{ruleCount === 1 ? '' : 's'}</span>
                </div>
                {extraction._source && (
                  <span className="text-xs text-gray-500">source: <code>{extraction._source}</code></span>
                )}
              </div>
              {sampleRules.length > 0 ? (
                <div className="overflow-x-auto rounded border border-gray-200 mb-2">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">row_id</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">rule_type</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">source / dep / value</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">filters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sampleRules.map((r, i) => {
                        const sourceCol = r.rule_type === 'reference_rate_output'
                          ? <code className="text-blue-700">{r.dependency?.type}:{r.dependency?.ref_id}</code>
                          : r.rule_type === 'manual_value'
                          ? <code className="text-amber-700">{String(r.manual_value ?? '')}</code>
                          : r.rule_type === 'expression'
                          ? <code className="text-green-700">{r.expression || ''}</code>
                          : <span className="font-mono text-gray-700">{(r.source_accounts || []).slice(0, 3).join(', ')}{(r.source_accounts || []).length > 3 ? '…' : ''}</span>;
                        return (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono text-blue-700">{r.row_id}</td>
                            <td className="px-2 py-1"><code className="text-blue-700">{r.rule_type}</code></td>
                            <td className="px-2 py-1">{sourceCol}</td>
                            <td className="px-2 py-1 text-gray-600">{(r.filters || []).length} filter(s)</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {ruleCount > sampleRules.length && (
                    <div className="px-2 py-1 text-xs text-gray-500">
                      … and {ruleCount - sampleRules.length} more rule(s)
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 mb-2">
                  AI returned 0 rules. The workbook may not have been recognized — check the format or re-upload.
                </div>
              )}
              {extraction.assumptions?.length > 0 && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded mb-2">
                  <div className="text-xs font-semibold text-amber-900 mb-1">AI assumptions:</div>
                  <ul className="text-xs list-disc ml-5 text-amber-900">
                    {extraction.assumptions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {extraction.warnings?.length > 0 && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="text-xs font-semibold text-yellow-900 mb-1">Warnings:</div>
                  <ul className="text-xs list-disc ml-5 text-yellow-900">
                    {extraction.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {compiledSched && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">Schedule {upper} configuration compiled</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-green-700">
              <div>Layout rows: {layoutRows}</div>
              <div>Rule entries: {compiledRuleCount}</div>
            </div>
          </div>
        )}
        <DependencyPanel preview={preview} />
        {compiledSched && <ExtractionPreview title={`Schedule ${upper} Rules Detail`} data={compiledSched} />}
      </>
    );
  };

  const renderScheduleHRules = () => {
    const schedH = compiledConfig?.config?.scheduleDefinitions?.schedule_h;
    const hPreview = dependencyPreviews['schedule_h'] || dependencyPreviews['schedule_h_rules'];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Schedule H Rules Upload</h2>
        <p className="text-gray-600 mb-6">
          Upload the Schedule H rules workbook. Claude extracts structured per-row rules and persists them to the intake; the configuration is then compiled automatically.
        </p>
        {renderScheduleRulesAIBox({
          scheduleLetter: 'h',
          fileType: 'schedule_h_rules',
          extraction: intake?.schedule_h_rules_extraction,
          compiledSched: schedH,
          preview: hPreview,
        })}
      </div>
    );
  };

  // ─── Screen 11: Schedule I Template ──────────────────────────────────────

  const renderScheduleITemplate = () => (
    <div>
      <h2 className="text-xl font-bold mb-4">Schedule I Template Upload</h2>
      <p className="text-gray-600 mb-6">
        Upload the Schedule I template that defines the indirect cost rate structure and summary layout.
      </p>

      {!enabledModules.schedule_i && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Schedule I is not enabled for this client. You can skip this step or enable it in Client Setup.
        </div>
      )}

      <FileUploadBox
        label="Schedule I Template (.xlsx)"
        onUpload={(file) => handleUpload('schedule_i_template', file)}
        uploading={uploading.schedule_i_template}
        result={uploadResults.schedule_i_template}
        existing={intake?.schedule_i_template_file_id || intake?.schedule_i_extraction ? 'Schedule I template uploaded' : null}
      />

      {intake?.schedule_i_extraction && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Layout Preview</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_i_extraction.sections?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Sections Identified</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_i_extraction.columns?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Columns Detected</p>
            </div>
          </div>
          <ExtractionPreview title="Schedule I Extraction" data={intake.schedule_i_extraction} />
        </div>
      )}
    </div>
  );

  // ─── Screen 12: Schedule I Rules ─────────────────────────────────────────

  const renderScheduleIRules = () => {
    const schedI = compiledConfig?.config?.scheduleDefinitions?.schedule_i;
    const iPreview = dependencyPreviews['schedule_i'] || dependencyPreviews['schedule_i_rules'];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Schedule I Rules Upload</h2>
        <p className="text-gray-600 mb-6">
          Upload the Schedule I rules workbook. Claude extracts structured per-row rules and persists them to the intake; the configuration is then compiled automatically.
        </p>
        {!enabledModules.schedule_i && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            Schedule I is not enabled for this client. You can skip this step or enable it in Client Setup.
          </div>
        )}
        {renderScheduleRulesAIBox({
          scheduleLetter: 'i',
          fileType: 'schedule_i_rules',
          extraction: intake?.schedule_i_rules_extraction,
          compiledSched: schedI,
          preview: iPreview,
        })}
      </div>
    );
  };

  // ─── Screen 13: Schedule K Template ──────────────────────────────────────

  const renderScheduleKTemplate = () => (
    <div>
      <h2 className="text-xl font-bold mb-4">Schedule K Template Upload</h2>
      <p className="text-gray-600 mb-6">
        Upload the Schedule K template that defines the facilities capital cost of money layout.
      </p>

      {!enabledModules.schedule_k && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          Schedule K is not enabled for this client. You can skip this step or enable it in Client Setup.
        </div>
      )}

      <FileUploadBox
        label="Schedule K Template (.xlsx)"
        onUpload={(file) => handleUpload('schedule_k_template', file)}
        uploading={uploading.schedule_k_template}
        result={uploadResults.schedule_k_template}
        existing={intake?.schedule_k_template_file_id ? 'Schedule K template uploaded' : null}
      />

      {intake?.schedule_k_extraction && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm text-gray-700 mb-3">Layout Preview</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_k_extraction.columns?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Columns Detected</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-900">
                {intake.schedule_k_extraction.rows?.length || 0}
              </p>
              <p className="text-xs text-blue-700">Rows Detected</p>
            </div>
          </div>
          <ExtractionPreview title="Schedule K Extraction" data={intake.schedule_k_extraction} />
        </div>
      )}
    </div>
  );

  // ─── Screen 14: Schedule K Rules ─────────────────────────────────────────

  const renderScheduleKRules = () => {
    const schedK = compiledConfig?.config?.scheduleDefinitions?.schedule_k;
    const kPreview = dependencyPreviews['schedule_k'] || dependencyPreviews['schedule_k_rules'];
    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Schedule K Rules Upload</h2>
        <p className="text-gray-600 mb-6">
          Upload the Schedule K rules workbook. Claude extracts structured per-row rules and persists them to the intake; the configuration is then compiled automatically.
        </p>
        {!enabledModules.schedule_k && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            Schedule K is not enabled for this client. You can skip this step or enable it in Client Setup.
          </div>
        )}
        {renderScheduleRulesAIBox({
          scheduleLetter: 'k',
          fileType: 'schedule_k_rules',
          extraction: intake?.schedule_k_rules_extraction,
          compiledSched: schedK,
          preview: kPreview,
        })}
      </div>
    );
  };

  // ─── Screen 16: Adequacy Checks ───────────────────────────────────────────

  const renderAdequacyChecks = () => {
    const checks = compiledConfig?.config?.adequacyChecks || [];
    const persistedChecks = intake?.adequacy_checks || [];
    const meta = intake?.adequacy_extraction_meta;
    const sampleChecks = persistedChecks.slice(0, 8);
    const isUploading = !!uploading.adequacy_check;
    const uploadResult = uploadResults.adequacy_check;

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Adequacy Check Rules Upload</h2>
        <p className="text-gray-600 mb-6">
          Upload the adequacy check workbook. Claude extracts each check (condition, severity, schedule reference) and persists them to the intake; the configuration is then compiled automatically.
        </p>

        <div className="border-2 border-blue-200 bg-blue-50/30 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-sm">AI extract adequacy checks from workbook</h4>
            <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">claude-opus-4-7</span>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Drop the adequacy checks workbook (typically a list of compliance items per ICS schedule).
            Claude will extract structured per-check entries (id, schedule, description, severity) and persist them.
          </p>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="file"
              accept=".xlsx,.xls"
              disabled={isUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload('adequacy_check', f);
              }}
              className="text-sm flex-1"
            />
            <span className="text-xs text-gray-500">
              {isUploading ? <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Extracting…</span> : 'Pick a file to extract'}
            </span>
          </div>
          {uploadResult?.error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-2">
              {uploadResult.error}
            </div>
          )}
          {uploadResult?.success && intake?.adequacy_check_file_id && (
            <div className="text-xs text-green-700 mb-2">Uploaded: <code className="bg-white px-1 rounded">{uploadResult.data?.filename}</code></div>
          )}

          {persistedChecks.length > 0 && (
            <div className="bg-white border rounded p-3 mt-2">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-gray-800">
                  AI extracted: <span className="text-blue-700">{persistedChecks.length} check{persistedChecks.length === 1 ? '' : 's'}</span>
                </div>
                {meta?._source && (
                  <span className="text-xs text-gray-500">source: <code>{meta._source}</code></span>
                )}
              </div>
              <div className="overflow-x-auto rounded border border-gray-200 mb-2">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">id</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">schedule</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">severity</th>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-600 border-b">description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleChecks.map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono text-blue-700 align-top whitespace-nowrap">{c.id || c.checkId || `check_${i + 1}`}</td>
                        <td className="px-2 py-1 align-top whitespace-nowrap">{c.schedule || '—'}</td>
                        <td className="px-2 py-1 align-top whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            c.severity === 'error' || c.severity === 'required' ? 'bg-red-100 text-red-800' :
                            c.severity === 'warning' ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>{c.severity || '—'}</span>
                        </td>
                        <td className="px-2 py-1 text-gray-700">{c.description || c.requirement || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {persistedChecks.length > sampleChecks.length && (
                  <div className="px-2 py-1 text-xs text-gray-500">
                    … and {persistedChecks.length - sampleChecks.length} more check(s)
                  </div>
                )}
              </div>
              {meta?.warnings?.length > 0 && (
                <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="text-xs font-semibold text-yellow-900 mb-1">Warnings:</div>
                  <ul className="text-xs list-disc ml-5 text-yellow-900">
                    {meta.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {meta?._llm_error && (
                <div className="p-2 bg-red-50 border border-red-200 rounded mt-2">
                  <div className="text-xs font-semibold text-red-900 mb-1">LLM error (used fallback):</div>
                  <div className="text-xs text-red-800">{meta._llm_error}</div>
                </div>
              )}
            </div>
          )}
          {persistedChecks.length === 0 && intake?.adequacy_check_file_id && !isUploading && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              Upload completed but no checks were extracted. Try re-uploading; if the issue persists, the workbook format may not be recognized.
            </div>
          )}
        </div>

        {checks.length > 0 && (
          <div className="mt-4">
            <h3 className="font-semibold text-sm text-gray-700 mb-3">
              Compiled Adequacy Checks ({checks.length})
            </h3>
            <div className="space-y-3">
              {checks.map((c, i) => (
                <div key={i} className="p-3 bg-white border rounded-lg text-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <span className="font-medium">{c.checkId || c.name || `Check ${i + 1}`}</span>
                      {c.description && <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {c.severity && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                          c.severity === 'error' ? 'bg-red-100 text-red-800' :
                          c.severity === 'warning' ? 'bg-amber-100 text-amber-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>{c.severity}</span>
                      )}
                      {(c.conditionType || c.condition?.check_type) && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800 whitespace-nowrap">
                          {c.conditionType || c.condition?.check_type}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Condition parameters */}
                  {c.condition && (
                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600 space-y-1">
                      {c.condition.target_field && (
                        <div><span className="font-medium">Target:</span> {c.condition.target_field}</div>
                      )}
                      {c.condition.operator && (
                        <div><span className="font-medium">Operator:</span> {c.condition.operator} {c.condition.threshold ?? ''}</div>
                      )}
                      {c.condition.threshold_low != null && (
                        <div><span className="font-medium">Range:</span> {c.condition.threshold_low} — {c.condition.threshold_high}</div>
                      )}
                      {c.condition.tolerance != null && (
                        <div><span className="font-medium">Tolerance:</span> {c.condition.tolerance}</div>
                      )}
                      {c.condition.row_ids?.length > 0 && (
                        <div><span className="font-medium">Row IDs:</span> {c.condition.row_ids.join(', ')}</div>
                      )}
                      {c.condition.source && (
                        <div><span className="font-medium">Source:</span> {c.condition.source.type} / {c.condition.source.ref_id}</div>
                      )}
                      {c.condition.target && c.condition.check_type === 'cross_reference_match' && (
                        <div><span className="font-medium">Cross-ref target:</span> {c.condition.target.type} / {c.condition.target.ref_id}</div>
                      )}
                      {c.condition.target_type && (
                        <div><span className="font-medium">Target type:</span> {c.condition.target_type}</div>
                      )}
                      {c.condition.required_fields?.length > 0 && (
                        <div><span className="font-medium">Required fields:</span> {c.condition.required_fields.join(', ')}</div>
                      )}
                      {c.condition.max_variance_pct != null && (
                        <div><span className="font-medium">Max variance:</span> {c.condition.max_variance_pct}% ({c.condition.direction || 'both'})</div>
                      )}
                      {c.condition.reference_label && (
                        <div><span className="font-medium">Reference:</span> {c.condition.reference_label} = {c.condition.reference_value}</div>
                      )}
                    </div>
                  )}
                  {c.failureMessage && (
                    <div className="mt-1 text-xs text-red-600">On failure: {c.failureMessage}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!compiledConfig && intake?.adequacy_check_file_id && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            Compile in the Review step to see the parsed check conditions.
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 17: Review Compiled Config ───────────────────────────────────

  const renderReview = () => {
    const config = compiledConfig?.config;
    const validation = compiledConfig?.validation;

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Review Compiled Configuration</h2>
        <p className="text-gray-600 mb-6">
          Compile and review the full client configuration before activation.
        </p>

        <div className="flex gap-3 mb-6">
          <button
            onClick={handleCompile}
            disabled={compiling}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {compiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {compiling ? 'Compiling...' : 'Compile Configuration'}
          </button>
          {config && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          )}
        </div>

        {config ? (
          <div className="space-y-4">
            {/* Validation Status */}
            <div className={`p-4 rounded-lg border ${validation?.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2">
                {validation?.valid ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`font-medium ${validation?.valid ? 'text-green-800' : 'text-red-800'}`}>
                  {validation?.valid ? 'Configuration is valid' : 'Configuration has errors'}
                </span>
              </div>
              {validation?.errors?.length > 0 && (
                <ul className="mt-2 text-sm text-red-700 list-disc pl-5">
                  {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {validation?.warnings?.length > 0 && (
                <ul className="mt-2 text-sm text-amber-700 list-disc pl-5">
                  {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </div>

            {/* Stale section warnings (Gap 4) */}
            {staleSections.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-800">
                    {staleSections.length} stale section{staleSections.length !== 1 ? 's' : ''} require revalidation before activation
                  </span>
                </div>
                {staleSections.map((s) => (
                  <div key={s.sectionId} className="flex items-center justify-between py-2 border-t border-amber-100">
                    <div>
                      <span className="font-mono text-sm">{s.sectionId}</span>
                      {s.staleReason && (
                        <span className="ml-2 text-xs text-amber-700">{s.staleReason}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleRevalidate(s.sectionId)}
                      disabled={revalidating[s.sectionId]}
                      className="text-xs px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded disabled:opacity-50"
                    >
                      {revalidating[s.sectionId] ? 'Revalidating...' : 'Revalidate'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 5-box summary counts */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-3 bg-cyan-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-cyan-900">{config.fringeDefinitions?.length || 0}</p>
                <p className="text-xs text-cyan-700">Fringes</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-900">{config.allocationDefinitions?.length || 0}</p>
                <p className="text-xs text-blue-700">Allocations</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-900">{config.overheadDefinitions?.length || 0}</p>
                <p className="text-xs text-amber-700">Overheads</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-900">{config.gaDefinition ? 1 : 0}</p>
                <p className="text-xs text-green-700">G&A</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-indigo-900">{Object.keys(config.scheduleDefinitions || {}).length}</p>
                <p className="text-xs text-indigo-700">Schedules</p>
              </div>
            </div>

            {/* Dependency graph + execution order */}
            {config.dependencyGraph && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-sm mb-2">Dependency Graph</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {config.dependencyGraph.nodes?.length || 0} nodes, {config.dependencyGraph.edges?.length || 0} edges
                </p>
                {config.dependencyGraph.nodes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-xs text-gray-500 uppercase mb-2">Execution Order</h4>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Topological sort from edges for display
                        const nodes = config.dependencyGraph.nodes || [];
                        const edges = config.dependencyGraph.edges || [];
                        const inDeg = {};
                        const adj = {};
                        nodes.forEach((n) => { const id = n.id || n; inDeg[id] = 0; adj[id] = []; });
                        edges.forEach((e) => { inDeg[e.to] = (inDeg[e.to] || 0) + 1; if (adj[e.from]) adj[e.from].push(e.to); });
                        const queue = Object.keys(inDeg).filter((k) => inDeg[k] === 0);
                        const order = [];
                        while (queue.length) {
                          const cur = queue.shift();
                          order.push(cur);
                          (adj[cur] || []).forEach((nb) => { inDeg[nb]--; if (inDeg[nb] === 0) queue.push(nb); });
                        }
                        // Add any remaining nodes not in graph
                        nodes.forEach((n) => { const id = n.id || n; if (!order.includes(id)) order.push(id); });
                        return order.map((id, idx) => (
                          <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-white border rounded text-xs">
                            <span className="font-bold text-blue-600">{idx + 1}.</span>
                            <span className="font-mono">{id}</span>
                          </span>
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Calculated outputs summary */}
            {(config.fringeDefinitions?.some((f) => f.outputs && Object.keys(f.outputs).length > 0) ||
              config.overheadDefinitions?.some((o) => o.outputs && Object.keys(o.outputs).length > 0) ||
              (config.gaDefinition?.outputs && Object.keys(config.gaDefinition.outputs).length > 0)) && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-sm text-blue-800 mb-3">Calculated Outputs</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {(config.fringeDefinitions || []).filter((f) => f.outputs).map((f) => (
                    Object.entries(f.outputs).map(([key, val]) => (
                      <div key={`${f.fringeId}-${key}`} className="p-2 bg-white rounded border text-sm">
                        <div className="text-xs text-cyan-600 font-medium">{f.fringeId}</div>
                        <div className="font-mono font-bold">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</div>
                        <div className="text-xs text-gray-500">{key}</div>
                      </div>
                    ))
                  ))}
                  {(config.overheadDefinitions || []).filter((o) => o.outputs).map((o) => (
                    Object.entries(o.outputs).map(([key, val]) => (
                      <div key={`${o.overheadId}-${key}`} className="p-2 bg-white rounded border text-sm">
                        <div className="text-xs text-amber-600 font-medium">{o.overheadId}</div>
                        <div className="font-mono font-bold">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</div>
                        <div className="text-xs text-gray-500">{key}</div>
                      </div>
                    ))
                  ))}
                  {config.gaDefinition?.outputs && Object.entries(config.gaDefinition.outputs).map(([key, val]) => (
                    <div key={`ga-${key}`} className="p-2 bg-white rounded border text-sm">
                      <div className="text-xs text-green-600 font-medium">{config.gaDefinition.gaId}</div>
                      <div className="font-mono font-bold">{typeof val === 'number' ? `${(val * 100).toFixed(4)}%` : String(val)}</div>
                      <div className="text-xs text-gray-500">{key}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fringe Details */}
            {config.fringeDefinitions?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Fringe Definitions</h3>
                {config.fringeDefinitions.map((f, i) => {
                  const s = sectionStatuses.find((ss) => ss.sectionId === f.fringeId);
                  return (
                    <div key={i} className="p-3 mb-2 bg-white border rounded-lg text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium font-mono">{f.fringeId}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{f.name}</span>
                          {s && <StatusBadge status={s.status} />}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        Pool: {describeAccountsOrModifiers(f.poolAccounts, f.poolModifiers, 'pool')} | Base: {describeAccountsOrModifiers(f.baseAccounts, f.poolModifiers, 'base')} | Filters: {f.filters?.length || 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Allocation Details */}
            {config.allocationDefinitions?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Allocation Definitions</h3>
                {config.allocationDefinitions.map((a, i) => {
                  const s = sectionStatuses.find((ss) => ss.sectionId === a.allocationId);
                  return (
                    <div key={i} className="p-3 mb-2 bg-white border rounded-lg text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium font-mono">{a.allocationId}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{a.name}</span>
                          {s && <StatusBadge status={s.status} />}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        Pool: [{a.poolAccounts?.join(', ')}] | Basis: {a.basis?.basisType || a.basisType || '—'} | Modifiers: {a.poolModifiers?.length || 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Overhead Details */}
            {config.overheadDefinitions?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Overhead Definitions</h3>
                {config.overheadDefinitions.map((o, i) => {
                  const s = sectionStatuses.find((ss) => ss.sectionId === o.overheadId);
                  return (
                    <div key={i} className="p-3 mb-2 bg-white border rounded-lg text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium font-mono">{o.overheadId}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{o.name}</span>
                          {s && <StatusBadge status={s.status} />}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-gray-600">
                        Pool: {describeAccountsOrModifiers(o.poolAccounts, o.poolModifiers, 'pool')} | Base: {describeAccountsOrModifiers(o.baseAccounts, o.poolModifiers, 'base')} | Filters: {o.filters?.length || 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* G&A */}
            {config.gaDefinition && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">G&A Definition</h3>
                <div className="p-3 bg-white border rounded-lg text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium font-mono">{config.gaDefinition.gaId}</span>
                    <span className="text-gray-500">{config.gaDefinition.name}</span>
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    Pool: {describeAccountsOrModifiers(config.gaDefinition.poolAccounts, config.gaDefinition.poolModifiers, 'pool')} | Base: {describeAccountsOrModifiers(config.gaDefinition.baseAccounts, config.gaDefinition.poolModifiers, 'base')}
                  </div>
                </div>
              </div>
            )}

            {/* Schedule expanded previews */}
            {Object.keys(config.scheduleDefinitions || {}).length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Schedule Definitions</h3>
                {Object.entries(config.scheduleDefinitions).map(([schedKey, schedDef]) => (
                  <ExtractionPreview
                    key={schedKey}
                    title={`${schedKey.toUpperCase()} — ${schedDef.layout?.rows?.length || 0} rows, ${schedDef.rules?.length || 0} rules`}
                    data={schedDef}
                  />
                ))}
              </div>
            )}

            {/* Adequacy checks list */}
            {config.adequacyChecks?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">
                  Adequacy Checks ({config.adequacyChecks.length})
                </h3>
                <div className="space-y-1">
                  {config.adequacyChecks.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-white border rounded text-sm">
                      <span>{c.checkId || c.name || `Check ${i + 1}`}</span>
                      {c.conditionType && (
                        <span className="px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">{c.conditionType}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Source artifacts grid */}
            {config.sourceArtifacts?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Source Artifacts</h3>
                <div className="grid grid-cols-2 gap-2">
                  {config.sourceArtifacts.map((a, i) => (
                    <div key={i} className="p-2 bg-white border rounded text-xs">
                      <span className="font-medium">{a.sourceName}</span>
                      <span className="text-gray-500 ml-2">{a.artifactType}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
            <Eye className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>Click "Compile Configuration" to generate and preview the compiled config.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Screen 18: Activate ──────────────────────────────────────────────────

  const renderActivate = () => {
    const config = compiledConfig?.config;
    const validation = compiledConfig?.validation;
    const canActivate = validation?.valid && !hasBlockingStale;

    // Post-activation parity warning view (soft warning — activation already
    // succeeded; the analyst reviews where production diverges from the
    // wizard's validated numbers before leaving the wizard).
    if (activationDone && parityWarning) {
      const fmt = (v) => (typeof v === 'number'
        ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : String(v ?? '—'));
      const pct = (m) => {
        const w = Number(m.wizard);
        const p = Number(m.production);
        if (!isFinite(w) || w === 0) return '—';
        return `${(((p - w) / Math.abs(w)) * 100).toFixed(2)}%`;
      };
      const mismatches = parityWarning.mismatches || [];
      return (
        <div>
          <h2 className="text-xl font-bold mb-4">Client Activated — Review Warnings</h2>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-green-800">The client is active.</span>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-800">
                Production numbers differ from the wizard for {mismatches.length} metric{mismatches.length !== 1 ? 's' : ''}
              </span>
            </div>
            <p className="text-sm text-amber-800 mb-3">
              {parityWarning.warning || 'Review these sections in production, or re-extract/recalculate the sections and re-activate.'}
            </p>
            <div className="overflow-x-auto rounded border border-amber-200 bg-white">
              <table className="w-full text-xs">
                <thead className="bg-amber-100/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-amber-900 border-b">Section</th>
                    <th className="px-3 py-2 text-left font-medium text-amber-900 border-b">Metric</th>
                    <th className="px-3 py-2 text-right font-medium text-amber-900 border-b">Wizard</th>
                    <th className="px-3 py-2 text-right font-medium text-amber-900 border-b">Production</th>
                    <th className="px-3 py-2 text-right font-medium text-amber-900 border-b">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {mismatches.map((m, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2 font-mono text-gray-700">{m.wizardKey || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{m.metric}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-700">{fmt(m.wizard)}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-700">{fmt(m.production)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{pct(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(parityWarning.skipped || []).length > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                {parityWarning.skipped.length} section(s) could not be compared (no matching production section).
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Done — Return to Clients
          </button>
        </div>
      );
    }

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Activate Client Configuration</h2>
        <p className="text-gray-600 mb-6">
          Promote the compiled configuration from draft to active status. All sections must be valid and non-stale.
        </p>

        {config ? (
          <div className="space-y-4">
            {/* Readiness check */}
            <div className={`p-4 rounded-lg border ${canActivate ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2">
                {canActivate ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
                <span className={`font-medium ${canActivate ? 'text-green-800' : 'text-red-800'}`}>
                  {canActivate
                    ? 'Ready for activation'
                    : hasBlockingStale
                    ? `${staleSections.length} stale section${staleSections.length !== 1 ? 's' : ''} must be revalidated before activation`
                    : 'Configuration has errors — fix before activating'}
                </span>
              </div>
              {validation?.errors?.length > 0 && (
                <ul className="mt-2 text-sm text-red-700 list-disc pl-5">
                  {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>

            {/* Stale blocking detail */}
            {hasBlockingStale && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-2">Stale sections blocking activation:</p>
                {staleSections.map((s) => (
                  <div key={s.sectionId} className="flex items-center justify-between py-1 text-sm">
                    <span className="font-mono text-amber-800">{s.sectionId}</span>
                    {s.staleReason && <span className="text-xs text-amber-600 ml-2">{s.staleReason}</span>}
                  </div>
                ))}
                <p className="text-xs text-amber-700 mt-2">Go to the Review step to revalidate stale sections.</p>
              </div>
            )}

            {/* Section summary */}
            <div className="grid grid-cols-5 gap-3 text-center text-sm">
              <div className="p-3 bg-gray-50 rounded">
                <strong>{config.fringeDefinitions?.length || 0}</strong>
                <div className="text-xs text-gray-500 mt-0.5">Fringes</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>{config.allocationDefinitions?.length || 0}</strong>
                <div className="text-xs text-gray-500 mt-0.5">Allocations</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>{config.overheadDefinitions?.length || 0}</strong>
                <div className="text-xs text-gray-500 mt-0.5">Overheads</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>{config.gaDefinition ? 'Yes' : 'No'}</strong>
                <div className="text-xs text-gray-500 mt-0.5">G&A</div>
              </div>
              <div className="p-3 bg-gray-50 rounded">
                <strong>{Object.keys(config.scheduleDefinitions || {}).length}</strong>
                <div className="text-xs text-gray-500 mt-0.5">Schedules</div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2 flex-wrap">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Draft
              </button>
              <button
                onClick={handleCompile}
                disabled={compiling}
                className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50"
              >
                {compiling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {compiling ? 'Validating...' : 'Final Validate'}
              </button>
              <button
                onClick={handleActivate}
                disabled={activating || !canActivate}
                className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {activating ? 'Activating...' : 'Activate Client Configuration'}
              </button>
            </div>

            {/* Version History (Gap 6) */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm text-gray-700">Version History</h3>
                <button
                  onClick={handleClone}
                  disabled={cloning}
                  className="flex items-center gap-2 text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  {cloning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                  {cloning ? 'Cloning...' : 'Clone from Active'}
                </button>
              </div>
              {versionHistory.length > 0 ? (
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-600 border-b">Version</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600 border-b">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600 border-b">Created</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-600 border-b">Activated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versionHistory.map((v) => (
                        <tr key={v.version} className="border-b last:border-0">
                          <td className="px-4 py-2 font-mono">v{v.version}</td>
                          <td className="px-4 py-2">
                            <StatusBadge status={v.status?.toUpperCase()} />
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {v.activatedAt ? new Date(v.activatedAt).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded border border-gray-200">
                  No version history available.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500 bg-yellow-50 rounded-lg border border-yellow-200">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-yellow-500" />
            <p>Go to the Review step first to compile and validate the configuration.</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Step renderer dispatch ───────────────────────────────────────────────
  // Static steps map by id. Dynamic section steps (fringe_1, allocation_3, etc.)
  // dispatch to the shared renderSection function.

  const STATIC_RENDERERS = {
    client_setup: renderClientSetup,
    gl_upload: renderGLUpload,
    gl_mapping: renderGLMapping,
    section_plan: renderSectionPlan,
    schedule_h_template: renderScheduleHTemplate,
    schedule_h_rules: renderScheduleHRules,
    schedule_i_template: renderScheduleITemplate,
    schedule_i_rules: renderScheduleIRules,
    schedule_k_template: renderScheduleKTemplate,
    schedule_k_rules: renderScheduleKRules,
    schedule_g_listing: renderScheduleGListing,
    schedule_l_listing: renderScheduleLListing,
    adequacy_checks: renderAdequacyChecks,
    review: renderReview,
    activate: renderActivate,
  };

  const renderStep = (step) => {
    if (!step) return <p>Step not implemented yet.</p>;
    if (step.sectionType) return renderSection(step);
    const render = STATIC_RENDERERS[step.id];
    return render ? render() : <p>Step not implemented yet.</p>;
  };

  // ─── Step completion ──────────────────────────────────────────────────────
  // Dynamic section steps are complete when either an AI proposal exists
  // for that sectionId OR the compiled config contains a matching definition.
  const isSectionStepComplete = (step) => {
    if (sectionAIState[step.id]?.proposal) return true;
    const cfg = compiledConfig?.config;
    if (!cfg) return false;
    if (step.sectionType === 'fringe') return (cfg.fringeDefinitions || []).some((d) => d.fringeId === step.id);
    if (step.sectionType === 'allocation') return (cfg.allocationDefinitions || []).some((d) => d.allocationId === step.id);
    if (step.sectionType === 'overhead' || step.sectionType === 'material_handling') return (cfg.overheadDefinitions || []).some((d) => d.overheadId === step.id);
    if (step.sectionType === 'ga') return !!cfg.gaDefinition;
    return false;
  };

  const stepCompletion = (() => {
    const result = {
      client_setup: !!intake,
      // Hard gate: not "done" until the async GL import finishes syncing rows.
      // Older intakes (uploaded synchronously, before gl_import_status existed)
      // have a gl_file_id but no status → treat as complete.
      gl_upload: !!intake?.gl_file_id
        && (intake?.gl_import_status ? intake.gl_import_status === 'completed' : true),
      gl_mapping: !!(intake?.gl_mapping_file_id || intake?.mapping_file_id),
      section_plan:
        !!intake?.gl_file_id && !!(intake?.gl_mapping_file_id || intake?.mapping_file_id),
      schedule_h_template: !!intake?.schedule_h_extraction,
      schedule_h_rules: !!(uploadResults.schedule_h_rules?.success || intake?.schedule_h_rules_file_id),
      schedule_i_template: !!intake?.schedule_i_extraction || !enabledModules.schedule_i,
      schedule_i_rules: !!(uploadResults.schedule_i_rules?.success || intake?.schedule_i_rules_file_id) || !enabledModules.schedule_i,
      schedule_k_template: !!intake?.schedule_k_template_file_id || !enabledModules.schedule_k,
      schedule_k_rules: !!(uploadResults.schedule_k_rules?.success || intake?.schedule_k_rules_file_id) || !enabledModules.schedule_k,
      schedule_g_listing: !!intake?.schedule_g_extraction || !enabledModules.schedule_g,
      schedule_l_listing: !!intake?.schedule_l_extraction || !enabledModules.schedule_l,
      adequacy_checks: !!intake?.adequacy_check_file_id,
      review: !!compiledConfig?.validation?.valid,
      activate: false,
    };
    // Section Plan is also done once any specific section has progress.
    for (const step of ALL_STEPS) {
      if (step.sectionType) {
        result[step.id] = isSectionStepComplete(step);
        if (result[step.id]) result.section_plan = true;
      }
    }
    return result;
  })();

  // ─── Loading ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold">Client Onboarding</h1>
              <p className="text-sm text-gray-500">
                Step {currentStepIdx + 1} of {ALL_STEPS.length}: {currentStep?.label}
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500 font-mono">{clientId}</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 flex gap-6">
        {/* Sidebar: Phase/Step navigation */}
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-lg border overflow-hidden sticky top-6">
            {phases.map((phase) => (
              <div key={phase.id}>
                <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b">
                  {phase.label}
                </div>
                {phase.steps.map((step) => {
                  const idx = ALL_STEPS.findIndex((s) => s.id === step.id);
                  const isCurrent = idx === currentStepIdx;
                  const isDone = stepCompletion[step.id] && !isCurrent;
                  const optDisabled = isOptionalDisabled(step.id);
                  return (
                    <button
                      key={step.id}
                      onClick={() => goToStep(idx)}
                      className={`w-full flex items-center gap-2 px-4 py-3 text-sm text-left border-b transition-colors
                        ${isCurrent ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-l-blue-600' : ''}
                        ${isDone && !isCurrent ? 'text-green-700' : ''}
                        ${!isCurrent && !isDone ? 'text-gray-600 hover:bg-gray-50' : ''}
                        ${optDisabled ? 'opacity-50' : ''}
                      `}
                    >
                      {isDone ? (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : isCurrent ? (
                        <div className="w-4 h-4 rounded-full bg-blue-600 flex-shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      )}
                      {step.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <button onClick={() => setError('')} className="p-1 hover:bg-red-100 rounded">
                <X className="w-4 h-4 text-red-500" />
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg border p-6 mb-6">
            {renderStep(currentStep)}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={goPrev}
              disabled={currentStepIdx === 0}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            <button
              onClick={goNext}
              disabled={currentStepIdx === ALL_STEPS.length - 1}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
